// Copyright © 2025–2026 Maestro Design & Management AB. Proprietary and confidential; see LICENSE.
//
// Space syntax (/space-syntax, permission space_syntax): angular segment
// analysis of the street network in a selected rectangle. Backend: the
// /space-syntax/start + status + analyses endpoints (space_syntax/handler.py,
// model in space_syntax/syntax.py). Each street segment carries NAIN
// (integration), NACH (choice / genomströmning) and NACH-B (choice between
// buildings, weighted by floor area - space_syntax/between.py) for three
// metric radii; the map colours them by rank. The Gaturum view shows each segment's sight
// length, width and enclosure from the building footprints
// (space_syntax/streetform.py). The Stadskvaliteter view shows what housing
// prices pay for - water, urban businesses, rail stations, barriers per
// segment and kvartersform per block (space_syntax/quality.py). Gaturum also
// has trängsel - through-movement against street width (streetform.py). The
// Räckvidd view judges the target group's needs for three walking paces (the
// svcD distances, weights.py) and the Gångvänlighet view counts the kinds of
// everyday destination within five minutes of each building
// (space_syntax/walkability.py) - after Space Syntax Ltd's CityZen Explorer,
// Walkability Index and active mobility capacity. Users with 'urbanitet:use'
// also get the urbanitetstal per kvarter (space_syntax/urbanitet.py) and the
// Gångflöde view - estimated pedestrians per day (space_syntax/pedestrians.py,
// Chalmers' Stockholm model), which counts Lantmäteriet property areas.
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useLoadScript, GoogleMap, Rectangle, Marker, InfoWindow } from '@react-google-maps/api';
import axios from 'axios';

import { GOOGLE_MAPS_API_KEY, DEFAULT_LOCATION } from '../constants/placeTypes';
import TopBar from './TopBar';
import API_BASE_URL from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { boundsAreaKm2, boundsToBox, boxToBounds, dimsLabelOf, lookupAreaName } from '../utils/selectionBox';
import { TARGET_GROUP_PRESETS } from '../constants/walkabilityPresets';
import { makeZip } from '../utils/zip';
import './OpenSpaceAnalyzer.css';   // shared toolbar/map/table building blocks (os-*)
import './SpaceSyntax.css';

// Must match the other map pages - the loader refuses a second set of options.
const libraries = ['places', 'geometry'];

const MAX_AREA_KM2 = 4.0;   // same caps as Tomma ytor (handler._validate_bbox)
const MIN_SIDE_M = 100;
const RADII = [400, 800, 1200];
const RADIUS_LABELS = { 400: '400 m', 800: '800 m', 1200: '1 200 m' };
const WALK_MIN = { 400: 5, 800: 10, 1200: 15 };
// label = the button, name = titles, legends and tables.
const MEASURES = {
  nain: { label: 'Integration', name: 'Integration', short: 'NAIN', low: 'Segregerat', high: 'Integrerat' },
  nach: { label: 'Genomströmning', name: 'Genomströmning', short: 'NACH', low: 'Lite genomfart', high: 'Mycket genomfart' },
  // between.py: every path counts the floor area at both its ends (jobs from 2026-11-02 on)
  nachB: { label: 'Mellan husen', name: 'Genomströmning mellan husen', short: 'NACH-B',
    low: 'Lite genomfart', high: 'Mycket genomfart' },
};
// depthmapX-style spectrum, blue (low) -> red (high), one colour per tenth.
const SPECTRUM = ['#2c3fd1', '#2b7de0', '#29b6e8', '#3fcf9c', '#8fd14f',
  '#d4e02f', '#f5c31f', '#f39021', '#ec5a1f', '#d7191c'];
const lineWidth = (band) => 1.5 + band * 0.45;
// Grey base map so the coloured network reads; satellite is one click away.
const MAP_STYLES = [
  { stylers: [{ saturation: -100 }, { lightness: 20 }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
];
const STATIC_STYLE = '&style=saturation:-100|lightness:20&style=feature:poi|visibility:off';
const ATTRIBUTION = '© OpenStreetMap-bidragsgivare (ODbL) · Bakgrundskarta © Google';
const PATH_HW = new Set(['footway', 'path', 'cycleway', 'steps', 'bridleway', 'track']);
const streetName = (p) => p.name || (PATH_HW.has(p.hw) ? 'Namnlös gång- och cykelväg' : 'Namnlös gata');
const fmt = (v, d = 2) => (v == null ? '–'
  : v.toLocaleString('sv-SE', { minimumFractionDigits: d, maximumFractionDigits: d }));
const lineParts = (g) => (g.type === 'LineString' ? [g.coordinates] : g.coordinates);
// One colour per service need (the order of the target group's needs).
const NEED_COLORS = ['#7b2cbf', '#e76f51', '#2a9d8f', '#264653', '#f4a261', '#9c6644',
  '#118ab2', '#ef476f', '#6a994e', '#3d405b'];
const speedText = (v) => (v === 0 ? 'bilfri' : v === 7 ? 'gångfart' : `${v} km/h`);
// Urbanitetstal classes: 0 = one property in the kvarter, >= 1 = the property
// lines inside are at least as long as the kvarter's edge.
const U_CLASSES = [
  { max: 0, color: '#c9c9c9', label: '0' },
  { max: 0.25, color: '#fde4c3', label: '0–0,25' },
  { max: 0.5, color: '#f9bc80', label: '0,25–0,5' },
  { max: 1, color: '#f08a4b', label: '0,5–1' },
  { max: 1.5, color: '#d6542b', label: '1–1,5' },
  { max: Infinity, color: '#9e2a12', label: '≥ 1,5' },
];
const uColor = (u) => (U_CLASSES.find(c => u <= c.max) || U_CLASSES[0]).color;
const kvarterTitle = (p) => p.name || 'Kvarter utan namn';
const areaText = (m2) => (m2 >= 10000 ? `${fmt(m2 / 10000, 1)} ha` : `${Math.round(m2).toLocaleString('sv-SE')} m²`);
const polyRings = (g) => (g.type === 'Polygon' ? [g.coordinates] : g.coordinates);
const URB_ATTRIBUTION = 'Fastighetsindelning och byggnader © Lantmäteriet';
// Gaturum (streetform.py): fixed classes, so areas compare on colour too.
// classes = the 9 bounds between the 10 SPECTRUM colours.
const FORM_MEASURES = {
  sightM: { label: 'Siktlängd', short: 'm', unit: ' m', d: 0, low: 'Kort sikt', high: 'Lång sikt',
    top: 'Längst sikt', bottom: 'Kortast sikt', classes: [50, 100, 150, 200, 300, 400, 550, 700, 850],
    legend: 'Fri sikt längs gatan, framåt + bakåt, median per segment' },
  widthM: { label: 'Gatubredd', short: 'm', unit: ' m', d: 0, low: 'Smal', high: 'Bred',
    top: 'Bredast', bottom: 'Smalast', classes: [8, 11, 14, 17, 20, 24, 28, 34, 45],
    legend: 'Fasad till fasad, median per segment · grått = öppet' },
  enclosure: { label: 'Rumslighet', short: 'H/B', unit: '', d: 2, low: 'Öppen', high: 'Sluten',
    top: 'Mest sluten', bottom: 'Mest öppen', classes: [0.2, 0.3, 0.4, 0.5, 0.65, 0.8, 1, 1.3, 1.7],
    legend: 'Fasadhöjd / gatubredd, median per segment · grått = öppet' },
  // streetform._pressure: NACH 800 rank in the box x 15 m / widthM (jobs from 2026-10-29 on)
  pressure: { label: 'Trängsel', short: 'Index', unit: '', d: 2, low: 'Rymligt', high: 'Trångt',
    top: 'Trängst', bottom: 'Rymligast', classes: [0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.85, 1, 1.3],
    legend: 'Genomströmning (800 m) mot gatubredd · 1 = rutans största stråk med 15 m mellan fasaderna · grått = öppet' },
};
const formBand = (fm, v) => upperBound(fm.classes, v);   // 0-9
const OPEN_COLOR = '#bdbdbd';
const FORM_ATTRIBUTION = 'Byggnader © Lantmäteriet · Byggnadshöjder NMD © Naturvårdsverket · Gator © OpenStreetMap-bidragsgivare (ODbL)';
// Stadskvaliteter (quality.py): what housing prices pay for, from Spacescape &
// Evidens's price models (Stockholm 2011, Göteborg 2016) and the Danish IFRO
// study (2013). Fixed classes as in Gaturum. For the distances short is the
// attractive end (reverse), so red always means "more of what buyers pay for".
// low/high = the texts at the blue and the red end of the legend.
const QUALITY_MEASURES = {
  attract: { label: 'Attraktivitet', unit: '', low: 'Låg', high: 'Hög', col: 'Poäng',
    classes: [10, 20, 30, 40, 50, 60, 70, 80, 90], none: '–',
    legend: 'Stadsutbud och vatten, 0–100 · kalibrerad på Östermalm, Södermalm och Rinkeby',
    top: 'Mest attraktivt', bottom: 'Minst attraktivt' },
  waterM: { label: 'Vatten', unit: ' m', reverse: true, low: 'Långt', high: 'Nära', col: 'Meter',
    classes: [100, 200, 300, 500, 750, 1000, 1500, 2000, 3000], none: 'inget vatten inom 3 km',
    legend: 'Gångavstånd till hav, sjö eller å på minst 5 ha', top: 'Närmast vatten', bottom: 'Längst till vatten' },
  bizN: { label: 'Stadsutbud', unit: '', low: 'Litet', high: 'Stort', col: 'Antal',
    classes: [10, 25, 50, 100, 200, 300, 500, 800, 1200], none: '–',
    legend: 'Restauranger, kaféer, butiker, service och kultur inom 1 km gångväg',
    top: 'Störst stadsutbud', bottom: 'Minst stadsutbud' },
  railM: { label: 'Spårstation', unit: ' m', reverse: true, low: 'Långt', high: 'Nära', col: 'Meter',
    classes: [200, 300, 400, 500, 750, 1000, 1500, 2000, 3000], none: 'ingen station inom 3 km',
    legend: 'Gångavstånd till station för tåg, tunnelbana, lokalbana eller spårvagn',
    top: 'Närmast spårstation', bottom: 'Längst till spårstation' },
  barrierM: { label: 'Barriärer', unit: ' m', low: 'Nära', high: 'Långt', col: 'Meter',
    classes: [50, 100, 150, 200, 300, 400, 600, 800, 1000], none: 'över 1 km',
    legend: 'Avstånd till motorväg eller järnväg ovan jord, fågelvägen',
    top: 'Längst från barriärer', bottom: 'Närmast barriärer' },
  edgeBuilt: { label: 'Kvartersform', unit: ' %', blocks: true, low: 'Öppet', high: 'Slutet',
    classes: [10, 20, 30, 40, 50, 60, 70, 80, 90],
    legend: 'Bebyggd andel av kvarterets yttersta 10 m mot gatan' },
};
const BARRIER_FAR = 1000;   // quality.BARRIER_MAX_M: neither within 1 km
// Attraktivitet: the two measures that best separated Östermalm and Södermalm
// from Rinkeby (and, untouched, Vasastan and Gamla stan from Tensta) in a
// comparison of 150 m cells, 2026-10-31 - kinds of urban business within 1 km
// (more = better) and walking distance to water (shorter = better). Each is
// turned into its percentile among the 113 built cells of those three boxes
// (5 % steps, interpolated; fixed so areas compare) and the score is the mean.
const ATTRACT_BIZ_TYPES = [21, 23, 23, 23, 30, 43, 49, 56, 64, 68, 72, 76, 79, 80, 83, 84, 86, 87, 89, 90, 91];
const ATTRACT_WATER_M = [55, 136, 251, 305, 447, 553, 651, 737, 820, 861, 903, 939, 986, 1032, 1079, 1168,
  1286, 3143, 3410, 3649, 3899];
const WATER_NONE_M = 3900;   // nothing within 3 km = 3 km x quality.CROW_DETOUR
const percentile = (q, x) => {
  if (x <= q[0]) return 0;
  if (x >= q[q.length - 1]) return 1;
  let i = 0;
  while (q[i + 1] <= x) i += 1;
  return (i + (x - q[i]) / (q[i + 1] - q[i])) / (q.length - 1);
};
const attractScore = (p) => (p.bizTypes == null ? null
  : 50 * (percentile(ATTRACT_BIZ_TYPES, p.bizTypes) + 1 - percentile(ATTRACT_WATER_M, p.waterM ?? WATER_NONE_M)));
// The shown value of a quality measure for a segment's (or block's) properties.
const qValue = (p, key) => {
  if (key === 'attract') return attractScore(p);
  if (key === 'barrierM') {
    const d = [p.motorwayM, p.railwayM].filter(v => v != null);
    return d.length ? Math.min(...d) : BARRIER_FAR;
  }
  if (key === 'edgeBuilt') return p.edgeBuilt == null ? null : 100 * p.edgeBuilt;
  return p[key] ?? null;
};
const qBand = (qm, v) => { const b = upperBound(qm.classes, v); return qm.reverse ? 9 - b : b; };   // 0-9
const qEnds = (qm) => {
  const n = (v) => `${v.toLocaleString('sv-SE')}${qm.unit}`;
  return qm.reverse ? [`${qm.low} ≥ ${n(qm.classes[8])}`, `${qm.high} < ${n(qm.classes[0])}`]
    : [`${qm.low} < ${n(qm.classes[0])}`, `${qm.high} ≥ ${n(qm.classes[8])}`];
};
const RAIL_MODES = { train: 'tåg', subway: 'tunnelbana', light_rail: 'lokalbana', tram: 'spårvagn' };
const QUALITY_ATTRIBUTION = 'Vatten, verksamheter, stationer, motorvägar och järnvägar © OpenStreetMap-bidragsgivare (ODbL) · Byggnader © Lantmäteriet';
// Räckvidd: the target group's needs judged for three walking paces - the same
// walking time reaches a shorter distance. Promenadavstånd's distances assume
// ordinary walking (~1.4 m/s, Geoapify's 'walk'); a rollator ~0.7 m/s
// (dp_analyzer/walking.ROLLATOR_SPEED_MS). svcD per segment = metres to each need.
const BASE_PACE_MS = 1.4;
const PACES = [
  { id: 'normal', label: 'Normal takt', ms: 1.4 },
  { id: 'slow', label: 'Långsam', ms: 1.0 },
  { id: 'rollator', label: 'Rollator', ms: 0.7 },
];
const paceReach = (need, pace) => need.maxMeters * pace.ms / BASE_PACE_MS;
const reachMet = (p, needs, pace) => needs.filter((n, k) => p.svcD?.[k] != null && p.svcD[k] <= paceReach(n, pace));
const reachBand = (share) => Math.round(9 * share);   // 0-9
// Gångvänlighet (walkability.py): buildings compact-encoded as
// [count, mask, x0, y0, dx, dy, ...] in 1e-5 degrees; bit k of mask =
// syntax.walk.kinds[k] within five minutes' walk.
const decodeWalk = (b, wid) => {
  const [n, mask, ...xy] = b;
  const ring = [];
  let x = 0, y = 0;
  for (let i = 0; i + 1 < xy.length; i += 2) {
    x += xy[i]; y += xy[i + 1];
    ring.push([x / 1e5, y / 1e5]);
  }
  if (ring.length) ring.push(ring[0]);
  return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: { n, mask, wid } };
};
const walkBand = (n, kinds) => (kinds ? Math.round(9 * n / kinds) : 0);   // 0-9
const WALK_ATTRIBUTION = 'Byggnader © Lantmäteriet · Verksamheter © OpenStreetMap-bidragsgivare (ODbL) · Platser © Google';
// Gångflöde (pedestrians.py): estimated pedestrians per weekday 07-22, fixed
// classes (the 9 bounds between the 10 SPECTRUM colours).
const PED_CLASSES = [100, 150, 250, 400, 600, 900, 1400, 2000, 3000];
const pedBand = (v) => upperBound(PED_CLASSES, v);   // 0-9
const pedText = (v) => (v == null ? '–' : `ca ${(v >= 1000 ? Math.round(v / 100) * 100 : Math.round(v / 10) * 10).toLocaleString('sv-SE')}`);
const PED_ATTRIBUTION = 'Gator och verksamheter © OpenStreetMap-bidragsgivare (ODbL) · Fastighetsindelning © Lantmäteriet · Hastigheter © Trafikverket';
// The backend stores the weighting factor wF instead of nachBW<r> (status answer size).
const withDerived = (geo) => {
  (geo?.features || []).forEach(({ properties: p }) => {
    if (p.wF == null) return;
    RADII.forEach((r) => {
      if (p[`nachB${r}`] != null) p[`nachBW${r}`] = Math.round(p[`nachB${r}`] * p.wF * 1e4) / 1e4;
    });
  });
  return geo;
};

// Index of the first element >= v in an ascending array
const lowerBound = (arr, v) => {
  let lo = 0, hi = arr.length;
  while (lo < hi) {
    const m = (lo + hi) >> 1;
    if (arr[m] < v) lo = m + 1; else hi = m;
  }
  return lo;
};
// Index of the first element > v
const upperBound = (arr, v) => {
  let lo = 0, hi = arr.length;
  while (lo < hi) {
    const m = (lo + hi) >> 1;
    if (arr[m] <= v) lo = m + 1; else hi = m;
  }
  return lo;
};
// Colour band 0-9 of a Space syntax value = the tenth of its rank in the box.
const rankBander = (features, key) => {
  const sorted = features.map(f => f.properties[key]).sort((a, b) => a - b);
  return (v) => (sorted.length ? Math.min(9, Math.floor((10 * lowerBound(sorted, v)) / sorted.length)) : 0);
};

const SpaceSyntaxTool = () => {
  const [initialView] = useState({ center: DEFAULT_LOCATION, zoom: 14 });
  const [selecting, setSelecting] = useState(false);
  const [cornerA, setCornerA] = useState(null);
  const [hoverCorner, setHoverCorner] = useState(null);
  const [lockedBox, setLockedBox] = useState(null);  // {wM, hM, dLat, dLng}
  const [mapCenter, setMapCenter] = useState(initialView.center);
  const [mapReady, setMapReady] = useState(false);
  const [includePaths, setIncludePaths] = useState(true);
  const [serviceGroup, setServiceGroup] = useState('core');
  // Weighted view: the values x speed factor x service factor (weights.py)
  const [weighted, setWeighted] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [status, setStatus] = useState('Välj ett område på kartan och tryck Analysera för att se hur gatunätet hänger ihop.');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);        // FeatureCollection of street segments
  const [summary, setSummary] = useState(null);      // job.syntax
  // The analysed rectangle. The on-screen box is screen-pinned (it slides off
  // the result when the map is panned), so the PNG frames this one instead.
  const [analysedBbox, setAnalysedBbox] = useState(null);
  const [measure, setMeasure] = useState('nain');
  const [radius, setRadius] = useState(800);
  const [selectedLink, setSelectedLink] = useState(null);     // {props, pos}
  const [selectedStreet, setSelectedStreet] = useState(null); // street name
  // Urbanitetstal view (own permission; the job computes it when granted)
  const { hasPermission } = useAuth();
  const canUrbanitet = hasPermission('urbanitet:use');
  const [view, setView] = useState('syntax');   // 'syntax' | 'ped' | 'form' | 'quality' | 'reach' | 'walk' | 'urbanitet'
  const [formKey, setFormKey] = useState('sightM');           // a FORM_MEASURES key
  const [qKey, setQKey] = useState('attract');                // a QUALITY_MEASURES key
  const [paceId, setPaceId] = useState('normal');             // a PACES id (Räckvidd)
  const [selectedBlock, setSelectedBlock] = useState(null);   // {props, pos}
  const [selectedBuilding, setSelectedBuilding] = useState(null); // {props, pos} (Gångvänlighet)
  const [urbSummary, setUrbSummary] = useState(null);         // job.urbanitet
  const [selectedKvarter, setSelectedKvarter] = useState(null); // {props, pos}
  const [previous, setPrevious] = useState([]);
  const [zipProgress, setZipProgress] = useState(null);   // {done, total} while the zip is drawn
  const [busy, setBusy] = useState(null);   // {since, expect} | null
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!busy) { setElapsed(0); return undefined; }
    setElapsed(0);
    const id = setInterval(() => setElapsed(Math.round((Date.now() - busy.since) / 1000)), 1000);
    return () => clearInterval(id);
  }, [busy]);

  const mapRef = useRef(null);
  const rectRef = useRef(null);
  const dataRef = useRef(null);
  const servicesRef = useRef(null);
  const kvarterRef = useRef(null);
  const blocksRef = useRef(null);
  const stationsRef = useRef(null);
  const walkRef = useRef(null);
  const mapCenterRef = useRef(initialView.center);
  const lockedBoxRef = useRef(null);
  lockedBoxRef.current = lockedBox;

  // Results from before the weighting have no nainW*/nachW* values.
  const hasWeights = !!result?.features?.length && result.features[0].properties.nainW800 != null;
  const showWeighted = weighted && hasWeights;
  // The gaturum factor (formF) is in the weighted values since 2026-10-29,
  // and only where building heights existed (weighting.formWeighted).
  const formWeighted = !!summary?.weighting?.formWeighted;
  const weightText = formWeighted ? 'hastighet, service och gaturum' : 'hastighet och service';
  // Genomströmning mellan husen: jobs from 2026-11-02 on, where the buildings could be read.
  const hasBetween = !!result?.features?.length && result.features[0].properties.nachB800 != null;
  const shownMeasure = measure === 'nachB' && !hasBetween ? 'nach' : measure;
  const key = `${shownMeasure}${showWeighted ? 'W' : ''}${radius}`;
  const needs = useMemo(() => summary?.weighting?.needs || [], [summary]);
  const needColor = (id) => NEED_COLORS[Math.max(0, needs.findIndex(n => n.id === id)) % NEED_COLORS.length];
  const needLabel = (id) => (needs.find(n => n.id === id) || {}).label || id;
  // Kvarter carry a client-side index (kid) so the table can select them.
  const kvarter = useMemo(() => (result?.urbanitet?.features || [])
    .map((f, kid) => ({ ...f, properties: { ...f.properties, kid } })), [result]);
  const hasUrbanitet = canUrbanitet && !!urbSummary;
  const showUrb = view === 'urbanitet' && hasUrbanitet;
  // Gaturum: results from before it have no syntax.form.
  const form = summary?.form || null;
  const showForm = view === 'form' && !!form;
  const fm = FORM_MEASURES[formKey];
  // Stadskvaliteter: results from before it have no syntax.quality.
  const quality = summary?.quality || null;
  const showQuality = view === 'quality' && !!quality;
  const qm = QUALITY_MEASURES[qKey];
  const showBlocks = showQuality && !!qm.blocks;
  // Blocks carry a client-side index (bid) so the selected one can be outlined.
  const blockFeatures = useMemo(() => (result?.blocks?.features || [])
    .map((f, bid) => ({ ...f, properties: { ...f.properties, bid } })), [result]);
  // Räckvidd: results from before it have no svcD; a failed service search has none.
  const hasReach = !!result?.features?.length && Array.isArray(result.features[0].properties.svcD)
    && needs.length > 0;
  const showReach = view === 'reach' && hasReach;
  const pace = PACES.find(p => p.id === paceId) || PACES[0];
  // Gångvänlighet: results from before it have no syntax.walk.
  const walk = summary?.walk || null;
  const showWalk = view === 'walk' && !!walk;
  const walkKinds = useMemo(() => walk?.kinds || [], [walk]);
  const walkFeatures = useMemo(() => (result?.walk?.b || []).map(decodeWalk), [result]);
  // Gångflöde: testers only (the backend strips it for everyone else).
  const ped = summary?.ped || null;
  const hasPed = canUrbanitet && !!ped;
  const showPed = view === 'ped' && hasPed;
  const showSyntax = !showUrb && !showForm && !showQuality && !showReach && !showWalk && !showPed;

  const liveMapCenter = () => {
    const c = mapRef.current?.getCenter();
    return c ? { lat: c.lat(), lng: c.lng() } : mapCenterRef.current;
  };

  const handleCenterChanged = () => {
    const c = liveMapCenter();
    mapCenterRef.current = c;
    if (lockedBoxRef.current) setMapCenter(c);
  };

  const currentBounds = () => (
    lockedBoxRef.current ? boxToBounds(lockedBoxRef.current, mapCenterRef.current) : null
  );

  useEffect(() => {
    axios.get(`${API_BASE_URL}/space-syntax/analyses`, { withCredentials: true })
      .then((resp) => setPrevious((resp.data.analyses || [])
        .filter(a => a.status === 'completed').slice(0, 10)))
      .catch(() => {});   // non-critical - list stays empty
  }, []);

  const handleMapClick = useCallback((e) => {
    if (!selecting) return;
    const p = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    if (!cornerA) {
      setCornerA(p);
      setHoverCorner(p);
      setStatus('Dra ut rektangeln och klicka för att sätta motsatta hörnet.');
      return;
    }
    const b = {
      south: Math.min(cornerA.lat, p.lat), north: Math.max(cornerA.lat, p.lat),
      west: Math.min(cornerA.lng, p.lng), east: Math.max(cornerA.lng, p.lng),
    };
    setLockedBox(boundsToBox(b, liveMapCenter()));
    setMapCenter(liveMapCenter());
    setCornerA(null);
    setHoverCorner(null);
    setSelecting(false);
    setStatus('Område valt. Dra/justera rutan eller tryck Analysera.');
  }, [selecting, cornerA]);

  const handleMouseMove = useCallback((e) => {
    if (selecting && cornerA) setHoverCorner({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, [selecting, cornerA]);

  const startSelection = () => {
    setSelecting(true);
    setCornerA(null);
    setHoverCorner(null);
    setLockedBox(null);
    setResult(null);
    setSummary(null);
    setUrbSummary(null);
    setSelectedLink(null);
    setSelectedStreet(null);
    setSelectedKvarter(null);
    setSelectedBlock(null);
    setSelectedBuilding(null);
    setError('');
    setStatus('Klicka på kartan för att sätta rektangelns första hörn.');
  };

  // Same feedback guard as Tomma ytor: adopt only real drags/resizes (> ~1.5 m).
  const handleRectBoundsChanged = () => {
    const rb = rectRef.current?.getBounds();
    const box = lockedBoxRef.current;
    if (!rb || !box) return;
    const b = {
      south: rb.getSouthWest().lat(), west: rb.getSouthWest().lng(),
      north: rb.getNorthEast().lat(), east: rb.getNorthEast().lng(),
    };
    const expected = boxToBounds(box, mapCenterRef.current);
    if (['south', 'west', 'north', 'east'].some(k => Math.abs(b[k] - expected[k]) > 1.5e-5)) {
      setLockedBox(boundsToBox(b, mapCenterRef.current));
    }
  };

  const pollStatus = async (jobId) => {
    // Overpass mirrors may need several attempts (up to ~7 min) - poll for 11.
    let misses = 0;
    for (let attempt = 0; attempt < 260; attempt++) {
      await new Promise(r => setTimeout(r, 2500));
      let resp;
      try {
        resp = await axios.get(`${API_BASE_URL}/space-syntax/status/${jobId}`, { withCredentials: true });
        misses = 0;
      } catch (e) {
        // A dropped poll must not abandon a job that is still running.
        if (++misses >= 4 || e.response?.status === 401 || e.response?.status === 403) throw e;
        continue;
      }
      const job = resp.data;
      if (job.status === 'completed') return job;
      if (job.status === 'failed') throw new Error(job.error || 'Analysen misslyckades');
      setStatus(job.progressNote || (job.status === 'processing' ? 'Hämtar gatunätet…' : 'Analysen köar…'));
    }
    throw new Error('Analysen tog för lång tid - försök igen.');
  };

  const showJobResult = (job) => {
    setResult(withDerived(job.geojson) || null);
    setSummary(job.syntax || null);
    setUrbSummary(job.urbanitet || null);
    const jobReach = Array.isArray(job.geojson?.features?.[0]?.properties?.svcD)
      && !!job.syntax?.weighting?.needs?.length;
    const v = (view === 'urbanitet' && !job.urbanitet) || (view === 'form' && !job.syntax?.form)
      || (view === 'quality' && !job.syntax?.quality) || (view === 'reach' && !jobReach)
      || (view === 'walk' && !job.syntax?.walk) || (view === 'ped' && !job.syntax?.ped) ? 'syntax' : view;
    setView(v);
    setAnalysedBbox(job.bbox || null);
    setSelectedLink(null);
    setSelectedStreet(null);
    setSelectedKvarter(null);
    setSelectedBlock(null);
    setSelectedBuilding(null);
    if (job.bbox) {
      const mid = { lat: (job.bbox.south + job.bbox.north) / 2, lng: (job.bbox.west + job.bbox.east) / 2 };
      setLockedBox(boundsToBox(job.bbox, mid));
      setMapCenter(mid);
      mapCenterRef.current = mid;
      mapRef.current?.fitBounds(new window.google.maps.LatLngBounds(
        { lat: job.bbox.south, lng: job.bbox.west }, { lat: job.bbox.north, lng: job.bbox.east }));
    }
    setStatus(viewStatus(v, job.syntax, job.urbanitet));
  };

  const viewStatus = (v, s, u) => {
    if (v === 'form') {
      return s?.form?.error ? 'Gaturummen kunde inte mätas för den här analysen.'
        : `${(s?.linkCount || 0).toLocaleString('sv-SE')} gatusegment mätta mot `
          + `${(s?.form?.buildings || 0).toLocaleString('sv-SE')} byggnader. Klicka på en gata för dess värden.`;
    }
    if (v === 'quality') {
      const q = s?.quality;
      return q?.error ? 'Stadskvaliteterna kunde inte beräknas för den här analysen.'
        : `Stadskvaliteter för ${(s?.linkCount || 0).toLocaleString('sv-SE')} gatusegment`
          + (q?.blocks?.count ? ` och ${q.blocks.count.toLocaleString('sv-SE')} kvarter` : '')
          + '. Klicka på en gata eller ett kvarter för dess värden.';
    }
    if (v === 'reach') {
      return `Vardagsbehoven för ${s?.weighting?.serviceGroupLabel || 'målgruppen'} i olika gångtakt. `
        + 'Klicka på en gata för avstånden.';
    }
    if (v === 'walk') {
      const w = s?.walk;
      return w?.error ? 'Gångvänligheten kunde inte beräknas för den här analysen.'
        : `${(w?.buildings || 0).toLocaleString('sv-SE')} hus i rutan. Klicka på ett hus för vad som finns inom fem minuter.`;
    }
    if (v === 'urbanitet') {
      return u?.error ? 'Urbanitetstalet kunde inte beräknas för den här analysen.'
        : `${(u?.kvarterCount || 0).toLocaleString('sv-SE')} kvarter i rutan. Klicka på ett kvarter för dess värden.`;
    }
    if (v === 'ped') {
      return s?.ped?.error ? 'Gångflödet kunde inte uppskattas för den här analysen.'
        : 'Uppskattat antal gående per vardag (07–22) på varje gata. Klicka på en gata för vad uppskattningen bygger på.';
    }
    s = s || {};
    return `${(s.linkCount || 0).toLocaleString('sv-SE')} gatusegment analyserade – `
      + `${((s.lengthM || 0) / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} km gatunät i rutan`
      + `${s.network === 'streets' ? ' (bara gator)' : ' (gator, gång- och cykelvägar)'}. `
      + 'Klicka på en gata för dess värden.';
  };

  const handleAnalyze = async () => {
    const b = currentBounds();
    if (!b) return;
    const areaKm2 = boundsAreaKm2(b);
    if (areaKm2 > MAX_AREA_KM2) {
      setError(`Området är ${areaKm2.toFixed(1)} km² - max ${MAX_AREA_KM2} km². Gör rektangeln mindre.`);
      return;
    }
    const { wM, hM } = boundsToBox(b, mapCenterRef.current);
    if (wM < MIN_SIDE_M || hM < MIN_SIDE_M) {
      setError(`Rutan måste vara minst ${MIN_SIDE_M} m på varje sida.`);
      return;
    }
    setError('');
    setIsAnalyzing(true);
    setResult(null);
    setSummary(null);
    setUrbSummary(null);
    setSelectedLink(null);
    setSelectedStreet(null);
    setSelectedKvarter(null);
    setSelectedBlock(null);
    setSelectedBuilding(null);
    setBusy({ since: Date.now(), expect: 'normalt 30–120 s' });
    setStatus('Startar analys…');
    try {
      const areaName = await lookupAreaName((b.south + b.north) / 2, (b.west + b.east) / 2);
      const startResp = await axios.post(`${API_BASE_URL}/space-syntax/start`,
        { bbox: b, name: areaName || '', includePaths, serviceGroup },
        { withCredentials: true });
      const job = await pollStatus(startResp.data.jobId);
      showJobResult(job);
      setPrevious(prev => [job, ...prev.filter(p => p.analysisId !== job.analysisId)].slice(0, 10));
    } catch (e) {
      setError(`Analysen misslyckades: ${e.response?.data?.error || e.message || 'Okänt fel'}`);
      setStatus('');
    } finally {
      setIsAnalyzing(false);
      setBusy(null);
    }
  };

  const loadPreviousAnalysis = async (analysisId) => {
    setError('');
    setIsAnalyzing(true);
    setStatus('Hämtar tidigare analys…');
    try {
      const resp = await axios.get(`${API_BASE_URL}/space-syntax/status/${analysisId}`, { withCredentials: true });
      showJobResult(resp.data);
    } catch (e) {
      setError('Kunde inte hämta analysen.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Sorted values of the shown measure: colour = tenth of the rank.
  const sortedValues = useMemo(() => (result
    ? result.features.map(f => f.properties[key]).sort((a, b) => a - b) : []), [result, key]);
  const band = useCallback((v) => (sortedValues.length
    ? Math.min(9, Math.floor((10 * lowerBound(sortedValues, v)) / sortedValues.length)) : 0), [sortedValues]);
  // Ties count in the segment's favour on both sides: a value shared by a
  // third of the segments (NACH 0 on every dead end) is "lägsta 33 %", not 1 %.
  const rankText = (v) => {
    const n = sortedValues.length;
    const below = lowerBound(sortedValues, v) / n;
    const atMost = upperBound(sortedValues, v) / n;
    return below >= 0.5
      ? `topp ${Math.max(1, Math.round(100 * (1 - below)))} % i rutan`
      : `lägsta ${Math.max(1, Math.round(100 * atMost))} % i rutan`;
  };

  // The network as one google.maps.Data layer: thousands of polylines stay
  // fast, and one style function recolours them on measure/radius change.
  useEffect(() => {
    if (!mapReady) return undefined;
    const g = window.google.maps;
    const data = new g.Data({ map: mapRef.current });
    dataRef.current = data;
    const listener = data.addListener('click', (e) => {
      const props = {};
      e.feature.forEachProperty((v, k) => { props[k] = v; });
      setSelectedLink({ props, pos: { lat: e.latLng.lat(), lng: e.latLng.lng() } });
    });
    // The service places behind the weighting, shown with the weighted view.
    const services = new g.Data({ map: null });
    servicesRef.current = services;
    // The kvarter of the urbanitetstal view.
    const kv = new g.Data({ map: null });
    kvarterRef.current = kv;
    const kvListener = kv.addListener('click', (e) => {
      const props = {};
      e.feature.forEachProperty((v, k) => { props[k] = v; });
      setSelectedKvarter({ props, pos: { lat: e.latLng.lat(), lng: e.latLng.lng() } });
    });
    // The kvartersform blocks and the rail stations of the stadskvaliteter view.
    const blocks = new g.Data({ map: null });
    blocksRef.current = blocks;
    const blockListener = blocks.addListener('click', (e) => {
      const props = {};
      e.feature.forEachProperty((v, k) => { props[k] = v; });
      setSelectedBlock({ props, pos: { lat: e.latLng.lat(), lng: e.latLng.lng() } });
    });
    const stations = new g.Data({ map: null });
    stationsRef.current = stations;
    // The buildings of the gångvänlighet view.
    const walkLayer = new g.Data({ map: null });
    walkRef.current = walkLayer;
    const walkListener = walkLayer.addListener('click', (e) => {
      const props = {};
      e.feature.forEachProperty((v, k) => { props[k] = v; });
      setSelectedBuilding({ props, pos: { lat: e.latLng.lat(), lng: e.latLng.lng() } });
    });
    return () => {
      g.event.removeListener(listener); data.setMap(null); services.setMap(null);
      g.event.removeListener(kvListener); kv.setMap(null);
      g.event.removeListener(blockListener); blocks.setMap(null); stations.setMap(null);
      g.event.removeListener(walkListener); walkLayer.setMap(null);
      dataRef.current = null; servicesRef.current = null; kvarterRef.current = null;
      blocksRef.current = null; stationsRef.current = null; walkRef.current = null;
    };
  }, [mapReady]);

  useEffect(() => {
    const data = dataRef.current;
    const services = servicesRef.current;
    const kv = kvarterRef.current;
    const blocks = blocksRef.current;
    const stations = stationsRef.current;
    const walkLayer = walkRef.current;
    if (!data) return;
    data.forEach(f => data.remove(f));
    services.forEach(f => services.remove(f));
    kv.forEach(f => kv.remove(f));
    blocks.forEach(f => blocks.remove(f));
    stations.forEach(f => stations.remove(f));
    walkLayer.forEach(f => walkLayer.remove(f));
    if (result) data.addGeoJson({ type: 'FeatureCollection', features: result.features });
    if (result?.services) services.addGeoJson(result.services);
    if (kvarter.length) kv.addGeoJson({ type: 'FeatureCollection', features: kvarter });
    if (blockFeatures.length) blocks.addGeoJson({ type: 'FeatureCollection', features: blockFeatures });
    if (result?.stations) stations.addGeoJson(result.stations);
    if (walkFeatures.length) walkLayer.addGeoJson({ type: 'FeatureCollection', features: walkFeatures });
  }, [result, kvarter, blockFeatures, walkFeatures, mapReady]);

  // The buildings of the gångvänlighet view, coloured by the share of kinds within reach.
  useEffect(() => {
    const walkLayer = walkRef.current;
    if (!walkLayer) return;
    const sel = selectedBuilding?.props?.wid;
    const kinds = walkKinds.length;
    walkLayer.setStyle((f) => {
      const on = f.getProperty('wid') === sel;
      return {
        fillColor: SPECTRUM[walkBand(f.getProperty('n'), kinds)],
        fillOpacity: 0.85,
        strokeColor: on ? '#111' : '#555',
        strokeWeight: on ? 3 : 0.4,
        zIndex: on ? 10 : 1,
        clickable: !selecting,
      };
    });
    walkLayer.setMap(showWalk ? mapRef.current : null);
  }, [showWalk, selectedBuilding, selecting, walkFeatures, walkKinds.length, mapReady]);

  // One view at a time: the street network, the kvarter or the blocks.
  useEffect(() => {
    const blocks = blocksRef.current;
    const stations = stationsRef.current;
    if (!blocks) return;
    const sel = selectedBlock?.props?.bid;
    blocks.setStyle((f) => {
      const on = f.getProperty('bid') === sel;
      const v = f.getProperty('edgeBuilt');
      return {
        fillColor: v == null ? OPEN_COLOR : SPECTRUM[qBand(QUALITY_MEASURES.edgeBuilt, 100 * v)],
        fillOpacity: 0.8,
        strokeColor: on ? '#111' : '#555',
        strokeWeight: on ? 3 : 0.6,
        zIndex: on ? 10 : 1,
        clickable: !selecting,
      };
    });
    blocks.setMap(showBlocks ? mapRef.current : null);
    stations.setStyle((f) => ({
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE, scale: 5,
        fillColor: '#fff', fillOpacity: 1, strokeColor: '#111', strokeWeight: 2,
      },
      title: `${f.getProperty('name') || 'Station'} (${RAIL_MODES[f.getProperty('mode')] || 'spår'})`,
      zIndex: 60,
      clickable: false,
    }));
    stations.setMap(showQuality && qKey === 'railM' ? mapRef.current : null);
  }, [showBlocks, showQuality, qKey, selectedBlock, selecting, result, mapReady]);

  useEffect(() => {
    const data = dataRef.current;
    const kv = kvarterRef.current;
    if (!data || !kv) return;
    const sel = selectedKvarter?.props?.kid;
    kv.setStyle((f) => {
      const on = f.getProperty('kid') === sel;
      return {
        fillColor: uColor(f.getProperty('u')),
        fillOpacity: 0.8,
        strokeColor: on ? '#111' : '#6b5a4a',
        strokeWeight: on ? 3 : 0.8,
        zIndex: on ? 10 : 1,
        clickable: !selecting,
      };
    });
    kv.setMap(showUrb ? mapRef.current : null);
    data.setMap(showUrb || showBlocks || showWalk ? null : mapRef.current);
  }, [showUrb, showBlocks, showWalk, selectedKvarter, selecting, kvarter, mapReady]);

  useEffect(() => {
    const services = servicesRef.current;
    if (!services) return;
    services.setStyle((f) => ({
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE, scale: 3.5,
        fillColor: needColor(f.getProperty('need')), fillOpacity: 1,
        strokeColor: '#fff', strokeWeight: 1.2,
      },
      title: `${f.getProperty('name')} (${needLabel(f.getProperty('need'))})`,
      zIndex: 50,
      clickable: false,
    }));
    services.setMap((showWeighted && showSyntax) || showReach ? mapRef.current : null);
  }, [showWeighted, showSyntax, showReach, summary, result, mapReady]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const data = dataRef.current;
    if (!data) return;
    data.setStyle((f) => {
      const dim = selectedStreet && f.getProperty('name') !== selectedStreet;
      if (showQuality) {
        const v = qValue({ [qKey]: f.getProperty(qKey), motorwayM: f.getProperty('motorwayM'),
          railwayM: f.getProperty('railwayM'), bizTypes: f.getProperty('bizTypes'),
          waterM: f.getProperty('waterM') }, qKey);
        const d = v == null ? -1 : qBand(qm, v);
        return {
          strokeColor: d < 0 ? OPEN_COLOR : SPECTRUM[d],
          strokeWeight: (d < 0 ? 1.2 : lineWidth(d)) + (selectedStreet && !dim ? 2 : 0),
          strokeOpacity: dim ? 0.2 : 0.95,
          zIndex: Math.max(d, 0) + (selectedStreet && !dim ? 20 : 0),
          clickable: !selecting,
        };
      }
      if (showReach) {
        const d = reachBand(reachMet({ svcD: f.getProperty('svcD') }, needs, pace).length / needs.length);
        return {
          strokeColor: SPECTRUM[d],
          strokeWeight: lineWidth(d) + (selectedStreet && !dim ? 2 : 0),
          strokeOpacity: dim ? 0.2 : 0.95,
          zIndex: d + (selectedStreet && !dim ? 20 : 0),
          clickable: !selecting,
        };
      }
      if (showForm || showPed) {
        const v = f.getProperty(showPed ? 'ped' : formKey);
        const d = v == null ? -1 : showPed ? pedBand(v) : formBand(fm, v);
        return {
          strokeColor: d < 0 ? OPEN_COLOR : SPECTRUM[d],
          strokeWeight: (d < 0 ? 1.2 : lineWidth(d)) + (selectedStreet && !dim ? 2 : 0),
          strokeOpacity: dim ? 0.2 : 0.95,
          zIndex: Math.max(d, 0) + (selectedStreet && !dim ? 20 : 0),
          clickable: !selecting,
        };
      }
      const d = band(f.getProperty(key));
      return {
        strokeColor: SPECTRUM[d],
        strokeWeight: lineWidth(d) + (selectedStreet && !dim ? 2 : 0),
        strokeOpacity: dim ? 0.2 : 0.95,
        zIndex: d + (selectedStreet && !dim ? 20 : 0),
        clickable: !selecting,
      };
    });
  }, [key, band, selectedStreet, selecting, result, mapReady, showForm, formKey, showQuality, qKey, showReach, paceId, showPed]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Named streets ranked by the shown measure (length-weighted over the
  // segments inside the box). Gaturum, stadskvaliteter and gångflöde: streets
  // only, measured segments only; distances rank the shortest first.
  const shownKey = showQuality ? qKey : showForm ? formKey : showPed ? 'ped' : key;
  const streetsOnly = showForm || showQuality || showPed;
  const streets = useMemo(() => {
    if (!result) return [];
    const agg = new Map();
    result.features.forEach((f) => {
      const p = f.properties;
      const v = showQuality ? qValue(p, shownKey) : p[shownKey];
      if (!p.name || v == null || (streetsOnly && PATH_HW.has(p.hw))) return;
      const a = agg.get(p.name) || { name: p.name, sum: 0, len: 0 };
      a.sum += v * p.insideM;
      a.len += p.insideM;
      agg.set(p.name, a);
    });
    const sign = showQuality && QUALITY_MEASURES[shownKey].reverse ? -1 : 1;
    return [...agg.values()].filter(a => a.len >= 100)
      .map(a => ({ ...a, value: a.sum / a.len }))
      .sort((x, y) => sign * (y.value - x.value));
  }, [result, shownKey, streetsOnly, showQuality]);
  const valueColor = (v) => SPECTRUM[showQuality ? qBand(qm, v) : showForm ? formBand(fm, v)
    : showPed ? pedBand(v) : band(v)];

  // Attraktivitet for the box: median over the street length (paths excluded,
  // as in the other stadskvalitet figures).
  const attractMedian = useMemo(() => {
    if (!quality || quality.error || !result) return null;
    const segs = result.features.map(f => f.properties).filter(p => !PATH_HW.has(p.hw))
      .map(p => ({ v: attractScore(p), len: p.insideM })).filter(s => s.v != null)
      .sort((a, b) => a.v - b.v);
    const half = segs.reduce((a, s) => a + s.len, 0) / 2;
    let acc = 0;
    return segs.find(s => (acc += s.len) >= half)?.v ?? null;
  }, [result, quality]);

  // Räckvidd per pace over the street length in the box (paths through parks
  // excluded, as in the gaturum and stadskvalitet figures).
  const reachStats = useMemo(() => {
    if (!hasReach) return null;
    const segs = result.features.map(f => f.properties).filter(p => !PATH_HW.has(p.hw));
    const len = segs.reduce((a, p) => a + p.insideM, 0) || 1;
    const share = (test) => segs.reduce((a, p) => a + (test(p) ? p.insideM : 0), 0) / len;
    return PACES.map(pc => ({
      pace: pc,
      all: share(p => reachMet(p, needs, pc).length === needs.length),
      mean: segs.reduce((a, p) => a + p.insideM * reachMet(p, needs, pc).length / needs.length, 0) / len,
      perNeed: needs.map((n, k) => share(p => p.svcD?.[k] != null && p.svcD[k] <= paceReach(n, pc))),
    }));
  }, [result, hasReach, needs]);
  const paceStats = reachStats?.find(r => r.pace.id === pace.id);
  // The kinds of everyday destination, the most often missing first.
  const walkRanked = useMemo(() => walkKinds.map((k, i) => ({ ...k, i }))
    .sort((a, b) => (a.share ?? 0) - (b.share ?? 0)), [walkKinds]);

  const focusStreet = (name) => {
    if (selectedStreet === name) { setSelectedStreet(null); return; }
    setSelectedStreet(name);
    setSelectedLink(null);
    const gb = new window.google.maps.LatLngBounds();
    result.features.filter(f => f.properties.name === name)
      .forEach(f => lineParts(f.geometry).forEach(part => part.forEach(([lng, lat]) => gb.extend({ lat, lng }))));
    if (!gb.isEmpty()) mapRef.current?.fitBounds(gb, 80);
  };

  // Kvarter ranked by urbanitetstal.
  const kvarterRanked = useMemo(() => [...kvarter].sort((a, b) => b.properties.u - a.properties.u), [kvarter]);

  const focusKvarter = (f) => {
    const gb = new window.google.maps.LatLngBounds();
    polyRings(f.geometry).forEach(poly => poly[0].forEach(([lng, lat]) => gb.extend({ lat, lng })));
    if (gb.isEmpty()) return;
    setSelectedKvarter({ props: f.properties, pos: gb.getCenter().toJSON() });
    mapRef.current?.fitBounds(gb, 140);
  };

  const switchView = (v) => {
    setView(v);
    setSelectedLink(null);
    setSelectedKvarter(null);
    setSelectedBlock(null);
    setSelectedBuilding(null);
    setSelectedStreet(null);
    setStatus(viewStatus(v, summary, urbSummary));
  };

  // Google Static Maps background for the analysed box (grey roadmap, or the
  // live satellite type) and the projection from lat/lng to its pixels. One
  // fetch serves every map of the zip.
  const loadBackground = async (b) => {
    const SIZE = 640, SCALE = 2, PX = SIZE * SCALE;
    const cLat = (b.south + b.north) / 2, cLng = (b.west + b.east) / 2;
    const mercX = (lng, z) => (lng + 180) / 360 * 256 * Math.pow(2, z);
    const mercY = (lat, z) => {
      const r = lat * Math.PI / 180;
      return (1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2 * 256 * Math.pow(2, z);
    };
    let zoom = 20;
    while (zoom > 3) {
      const spanX = Math.abs(mercX(b.east, zoom) - mercX(b.west, zoom)) * SCALE;
      const spanY = Math.abs(mercY(b.south, zoom) - mercY(b.north, zoom)) * SCALE;
      if (spanX < PX * 0.9 && spanY < PX * 0.82) break;
      zoom -= 1;
    }
    const toPx = (lat, lng) => ({
      x: (mercX(lng, zoom) - mercX(cLng, zoom)) * SCALE + PX / 2,
      y: (mercY(lat, zoom) - mercY(cLat, zoom)) * SCALE + PX / 2,
    });
    const liveType = mapRef.current?.getMapTypeId?.() || 'roadmap';
    const mapType = ['roadmap', 'satellite', 'hybrid', 'terrain'].includes(liveType) ? liveType : 'roadmap';
    const url = `https://maps.googleapis.com/maps/api/staticmap?center=${cLat},${cLng}`
      + `&zoom=${zoom}&size=${SIZE}x${SIZE}&scale=${SCALE}&maptype=${mapType}`
      + `${mapType === 'roadmap' ? STATIC_STYLE : ''}&key=${GOOGLE_MAPS_API_KEY}`;
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error('Kunde inte hämta kartbilden (Static Maps)'));
      im.src = url;
    });
    return { img, toPx, PX, b, cLat, cLng };
  };

  // A map = a view and its measure (Space syntax: measure, radius, weighted;
  // Gaturum: formKey; Stadskvaliteter: qKey; Räckvidd: paceId).
  const shownSpec = () => ({
    view: showUrb ? 'urbanitet' : showForm ? 'form' : showQuality ? 'quality'
      : showReach ? 'reach' : showWalk ? 'walk' : showPed ? 'ped' : 'syntax',
    measure: shownMeasure, radius, weighted: showWeighted, formKey, qKey, paceId,
  });
  // Every map the result has, in the page's order (the measure buttons a
  // result disables are left out).
  const allSpecs = () => {
    const base = { measure: shownMeasure, radius, weighted: false, formKey, qKey, paceId };
    const specs = [];
    const measures = Object.keys(MEASURES).filter(ms => ms !== 'nachB' || hasBetween);
    [false, ...(hasWeights ? [true] : [])].forEach(w => measures.forEach(ms => RADII
      .forEach(r => specs.push({ ...base, view: 'syntax', measure: ms, radius: r, weighted: w }))));
    if (hasPed && !ped.error) specs.push({ ...base, view: 'ped' });
    if (form && !form.error) {
      Object.keys(FORM_MEASURES).filter(k => k !== 'pressure' || form.pinchShare != null)
        .forEach(k => specs.push({ ...base, view: 'form', formKey: k }));
    }
    if (quality && !quality.error) {
      Object.entries(QUALITY_MEASURES).filter(([, v]) => !v.blocks || blockFeatures.length)
        .forEach(([k]) => specs.push({ ...base, view: 'quality', qKey: k }));
    }
    if (hasReach) PACES.forEach(pc => specs.push({ ...base, view: 'reach', paceId: pc.id }));
    if (walk && !walk.error) specs.push({ ...base, view: 'walk' });
    if (hasUrbanitet && !urbSummary.error) specs.push({ ...base, view: 'urbanitet' });
    return specs;
  };
  const mapTitle = (s) => (s.view === 'urbanitet' ? 'Urbanitetstal'
    : s.view === 'form' ? `Gaturum ${FORM_MEASURES[s.formKey].label}`
      : s.view === 'quality' ? `Stadskvaliteter ${QUALITY_MEASURES[s.qKey].label}`
        : s.view === 'reach' ? `Räckvidd ${(PACES.find(p => p.id === s.paceId) || PACES[0]).label}`
          : s.view === 'walk' ? 'Gångvänlighet'
            : s.view === 'ped' ? 'Gångflöde'
              : `${s.weighted ? 'Viktad ' : ''}${MEASURES[s.measure].name} ${s.radius} m`);

  // PNG of one map: the background with the coloured network, box, legend
  // and attribution drawn on top.
  const drawMap = async (spec, bg) => {
    const { img, toPx, PX, b } = bg;
    const isUrb = spec.view === 'urbanitet', isForm = spec.view === 'form';
    const isQuality = spec.view === 'quality', isReach = spec.view === 'reach';
    const isWalk = spec.view === 'walk', isSyntax = spec.view === 'syntax', isPed = spec.view === 'ped';
    const sqm = QUALITY_MEASURES[spec.qKey], sfm = FORM_MEASURES[spec.formKey];
    const spc = PACES.find(p => p.id === spec.paceId) || PACES[0];
    const sm = MEASURES[spec.measure];
    const skey = `${spec.measure}${spec.weighted ? 'W' : ''}${spec.radius}`;
    const sband = rankBander(result.features, skey);
    const canvas = document.createElement('canvas');
    canvas.width = PX; canvas.height = PX;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, PX, PX);

    // Low values first so the red main streets end up on top.
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (isUrb) {
      kvarter.forEach((f) => {
        polyRings(f.geometry).forEach((poly) => {
          ctx.beginPath();
          poly.forEach((ring) => ring.forEach(([lng, lat], j) => {
            const p = toPx(lat, lng);
            j ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y);
          }));
          ctx.globalAlpha = 0.85;
          ctx.fillStyle = uColor(f.properties.u);
          ctx.fill('evenodd');
          ctx.globalAlpha = 1;
          ctx.strokeStyle = '#6b5a4a';
          ctx.lineWidth = 1.5;
          ctx.stroke();
        });
      });
    } else if (isQuality && sqm.blocks) {
      blockFeatures.forEach((f) => {
        polyRings(f.geometry).forEach((poly) => {
          ctx.beginPath();
          poly.forEach((ring) => ring.forEach(([lng, lat], j) => {
            const p = toPx(lat, lng);
            j ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y);
          }));
          ctx.globalAlpha = 0.85;
          ctx.fillStyle = SPECTRUM[qBand(sqm, qValue(f.properties, spec.qKey))];
          ctx.fill('evenodd');
          ctx.globalAlpha = 1;
          ctx.strokeStyle = '#555';
          ctx.lineWidth = 1.2;
          ctx.stroke();
        });
      });
    } else if (isWalk) {
      walkFeatures.forEach((f) => {
        ctx.beginPath();
        f.geometry.coordinates[0].forEach(([lng, lat], j) => {
          const p = toPx(lat, lng);
          j ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y);
        });
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = SPECTRUM[walkBand(f.properties.n, walkKinds.length)];
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = '#555';
        ctx.lineWidth = 0.6;
        ctx.stroke();
      });
    } else {
      const valueOf = (p) => (isReach ? reachMet(p, needs, spc).length / needs.length
        : isQuality ? qValue(p, spec.qKey) : isForm ? p[spec.formKey] : isPed ? p.ped : p[skey]);
      const bandOf = (v) => (isReach ? reachBand(v) : isQuality ? qBand(sqm, v)
        : isForm ? formBand(sfm, v) : isPed ? pedBand(v) : sband(v));
      [...result.features].sort((x, y) => {
        const a = valueOf(x.properties), c = valueOf(y.properties);
        return (a == null ? -1 : bandOf(a)) - (c == null ? -1 : bandOf(c));
      }).forEach((f) => {
        const v = valueOf(f.properties);
        const open = (isForm || isQuality || isPed) && v == null;
        const d = open ? 0 : bandOf(v);
        ctx.strokeStyle = open ? OPEN_COLOR : SPECTRUM[d];
        ctx.lineWidth = open ? 2 : lineWidth(d) * 1.6;
        lineParts(f.geometry).forEach((part) => {
          ctx.beginPath();
          part.forEach(([lng, lat], j) => { const p = toPx(lat, lng); j ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y); });
          ctx.stroke();
        });
      });
    }
    if (isQuality && spec.qKey === 'railM') {
      (result.stations?.features || []).forEach((f) => {
        const [lng, lat] = f.geometry.coordinates;
        const p = toPx(lat, lng);
        ctx.beginPath();
        ctx.arc(p.x, p.y, 8, 0, 2 * Math.PI);
        ctx.fillStyle = '#fff';
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#111';
        ctx.stroke();
      });
    }
    if ((isSyntax && spec.weighted) || isReach) {
      (result.services?.features || []).forEach((f) => {
        const [lng, lat] = f.geometry.coordinates;
        const p = toPx(lat, lng);
        ctx.beginPath();
        ctx.arc(p.x, p.y, 7, 0, 2 * Math.PI);
        ctx.fillStyle = needColor(f.properties.need);
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
      });
    }
    const p1 = toPx(b.north, b.west), p2 = toPx(b.south, b.east);
    ctx.strokeStyle = '#2979FF';
    ctx.lineWidth = 4;
    ctx.strokeRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);

    // Licence attribution, top-left - the bottom edge is Google's. Drawn
    // after the legend; measured first so a long one pushes the legend down.
    const attribution = isUrb ? `${URB_ATTRIBUTION} · Bakgrundskarta © Google`
      : isForm ? `${FORM_ATTRIBUTION} · Bakgrundskarta © Google`
        : isQuality ? `${QUALITY_ATTRIBUTION} · Bakgrundskarta © Google`
          : isWalk ? `${WALK_ATTRIBUTION} · Bakgrundskarta © Google`
            : isPed ? `${PED_ATTRIBUTION} · Bakgrundskarta © Google`
              : isReach ? `${ATTRIBUTION} · Platser © Google` : ATTRIBUTION;
    ctx.font = '15px sans-serif';
    const tw = ctx.measureText(attribution).width;

    // Legend panel, top-right. Urbanitetstal also carries the box's
    // attraktivitet (two more lines) when the job has stadskvaliteter.
    const urbAttract = isUrb && attractMedian != null;
    const W = isSyntax ? (spec.measure === 'nachB' ? 600 : spec.weighted ? 520 : 430) : 520, X = PX - W - 20;
    const Y = tw + 26 > X ? 46 : 20, H = urbAttract ? 178 : 128;
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = '#fff';
    ctx.fillRect(X, Y, W, H);
    ctx.globalAlpha = 1;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#111';
    ctx.font = 'bold 24px sans-serif';
    if (isUrb) {
      ctx.fillText('Urbanitetstal per kvarter', X + 16, Y + 14);
      ctx.fillStyle = '#555';
      ctx.font = '15px sans-serif';
      ctx.fillText(`Inre fastighetsgränser / kvarterets omkrets · området ${fmt(urbSummary?.u)}`, X + 16, Y + 46, W - 32);
      const cw = (W - 32) / U_CLASSES.length;
      U_CLASSES.forEach((c, i) => {
        ctx.fillStyle = c.color;
        ctx.fillRect(X + 16 + i * cw, Y + 72, cw - 2, 16);
        ctx.fillStyle = '#333';
        ctx.font = '13px sans-serif';
        ctx.fillText(c.label, X + 16 + i * cw, Y + 96);
      });
      if (urbAttract) {
        ctx.strokeStyle = '#ddd';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(X + 16, Y + 122);
        ctx.lineTo(X + W - 16, Y + 122);
        ctx.stroke();
        ctx.fillStyle = '#111';
        ctx.font = 'bold 18px sans-serif';
        ctx.fillText(`Attraktivitet ${fmt(attractMedian, 0)} av 100`, X + 16, Y + 130);
        ctx.fillStyle = '#555';
        ctx.font = '13px sans-serif';
        ctx.fillText('Stadsutbud och vatten, median längs gatorna · Östermalm, Södermalm ≈ 60, Rinkeby ≈ 10',
          X + 16, Y + 155, W - 32);
      }
    } else if (isForm || isPed) {
      ctx.fillText(isPed ? 'Gångflöde, gående per vardag' : `Gaturum: ${sfm.label}`, X + 16, Y + 14);
      ctx.fillStyle = '#555';
      ctx.font = '13px sans-serif';
      ctx.fillText(isPed ? `Uppskattning 07–22 (Chalmers Stockholmsmodell) · median längs gatorna ${pedText(ped?.medianDay)}`
        : sfm.legend, X + 16, Y + 48, W - 32);
      const sw = (W - 32) / 10;
      SPECTRUM.forEach((c, i) => { ctx.fillStyle = c; ctx.fillRect(X + 16 + i * sw, Y + 72, sw - 2, 16); });
      ctx.fillStyle = '#333';
      ctx.font = '14px sans-serif';
      ctx.fillText(isPed ? `Färre än ${PED_CLASSES[0]}` : `${sfm.low} < ${fmt(sfm.classes[0], sfm.d)}${sfm.unit}`, X + 16, Y + 96);
      ctx.textAlign = 'right';
      ctx.fillText(isPed ? `${PED_CLASSES[8].toLocaleString('sv-SE')} eller fler`
        : `${sfm.high} ≥ ${fmt(sfm.classes[8], sfm.d)}${sfm.unit}`, X + W - 16, Y + 96);
    } else if (isReach || isWalk) {
      ctx.fillText(isReach ? `Räckvidd: ${summary?.weighting?.serviceGroupLabel || ''} · ${spc.label.toLowerCase()}`
        : 'Gångvänlighet per hus', X + 16, Y + 14);
      ctx.fillStyle = '#555';
      ctx.font = '13px sans-serif';
      ctx.fillText(isReach ? `Andel av behoven inom räckhåll i ${String(spc.ms).replace('.', ',')} m/s`
        : 'Olika slags vardagsmål inom 5 minuters gång (400 m)', X + 16, Y + 48, W - 32);
      const sw = (W - 32) / 10;
      SPECTRUM.forEach((c, i) => { ctx.fillStyle = c; ctx.fillRect(X + 16 + i * sw, Y + 72, sw - 2, 16); });
      ctx.fillStyle = '#333';
      ctx.font = '14px sans-serif';
      ctx.fillText(isReach ? 'Inget behov' : 'Inget', X + 16, Y + 96);
      ctx.textAlign = 'right';
      ctx.fillText(isReach ? 'Alla behov' : `Alla ${walkKinds.length} slag`, X + W - 16, Y + 96);
    } else if (isQuality) {
      const [lowText, highText] = qEnds(sqm);
      ctx.fillText(`Stadskvaliteter: ${sqm.label}`, X + 16, Y + 14);
      ctx.fillStyle = '#555';
      ctx.font = '13px sans-serif';
      ctx.fillText(sqm.legend, X + 16, Y + 48, W - 32);
      const sw = (W - 32) / 10;
      SPECTRUM.forEach((c, i) => { ctx.fillStyle = c; ctx.fillRect(X + 16 + i * sw, Y + 72, sw - 2, 16); });
      ctx.fillStyle = '#333';
      ctx.font = '14px sans-serif';
      ctx.fillText(lowText, X + 16, Y + 96);
      ctx.textAlign = 'right';
      ctx.fillText(highText, X + W - 16, Y + 96);
    } else {
      ctx.fillText(`${spec.weighted ? 'Viktad ' + sm.name.toLowerCase() : sm.name} · ${RADIUS_LABELS[spec.radius]}`, X + 16, Y + 14, W - 32);
      ctx.fillStyle = '#555';
      ctx.font = '15px sans-serif';
      ctx.fillText(spec.weighted
        ? `${sm.short} × hastighet × service (${summary?.weighting?.serviceGroupLabel || ''})${formWeighted ? ' × gaturum' : ''}, rang i tiondelar`
        : `Space syntax (${sm.short}), rang inom rutan i tiondelar`, X + 16, Y + 46, W - 32);
      const sw = (W - 32) / 10;
      SPECTRUM.forEach((c, i) => { ctx.fillStyle = c; ctx.fillRect(X + 16 + i * sw, Y + 72, sw - 2, 16); });
      ctx.fillStyle = '#333';
      ctx.font = '14px sans-serif';
      ctx.fillText(sm.low, X + 16, Y + 96);
      ctx.textAlign = 'right';
      ctx.fillText(sm.high, X + W - 16, Y + 96);
    }

    ctx.font = '15px sans-serif';
    ctx.textAlign = 'left';
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = '#fff';
    ctx.fillRect(10, 10, tw + 16, 26);
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#222';
    ctx.fillText(attribution, 18, 15);

    return new Promise(r => canvas.toBlob(r, 'image/png'));
  };

  const saveBlob = (blob, name) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  };
  const stamp = () => new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');

  const exportPng = async () => {
    const b = analysedBbox || currentBounds();
    if (!b || !result) return;
    setError('');
    try {
      const bg = await loadBackground(b);
      const spec = shownSpec();
      const blob = await drawMap(spec, bg);
      const areaName = await lookupAreaName(bg.cLat, bg.cLng);
      saveBlob(blob, `${areaName || 'Gatunät'} ${mapTitle(spec)} ${stamp()}.png`);
    } catch (err) {
      setError(`PNG-export misslyckades: ${err.message}`);
    }
  };

  // Alla kartor: every view and measure of the result as numbered PNGs in one zip.
  const exportZip = async () => {
    const b = analysedBbox || currentBounds();
    if (!b || !result) return;
    setError('');
    const specs = allSpecs();
    setZipProgress({ done: 0, total: specs.length });
    try {
      const bg = await loadBackground(b);
      const files = [];
      for (let i = 0; i < specs.length; i++) {
        const blob = await drawMap(specs[i], bg);
        files.push({ name: `${String(i + 1).padStart(2, '0')} ${mapTitle(specs[i])}.png`, blob });
        setZipProgress({ done: i + 1, total: specs.length });
      }
      const areaName = await lookupAreaName(bg.cLat, bg.cLng);
      saveBlob(await makeZip(files), `${areaName ? `${areaName} ` : ''}Gatunät alla kartor ${stamp()}.zip`);
    } catch (err) {
      setError(`Zip-export misslyckades: ${err.message}`);
    } finally {
      setZipProgress(null);
    }
  };

  const bounds = lockedBox ? boxToBounds(lockedBox, mapCenter) : null;
  const selBounds = bounds || (cornerA && hoverCorner ? {
    south: Math.min(cornerA.lat, hoverCorner.lat), north: Math.max(cornerA.lat, hoverCorner.lat),
    west: Math.min(cornerA.lng, hoverCorner.lng), east: Math.max(cornerA.lng, hoverCorner.lng),
  } : null);
  const selAreaKm2 = bounds ? boundsAreaKm2(bounds) : 0;
  const dimsLabel = dimsLabelOf(selBounds);
  const m = MEASURES[shownMeasure];
  const top = streets.slice(0, 10);
  const bottom = streets.length > 15 ? streets.slice(-5).reverse() : [];

  const streetRow = (s, i) => (
    <tr key={s.name} onClick={() => focusStreet(s.name)} data-tip="ss.row"
        className={selectedStreet === s.name ? 'os-row-selected' : ''}>
      <td>{i}</td>
      <td><span className="ss-swatch" style={{ background: valueColor(s.value) }} />{s.name}</td>
      <td>{showPed ? pedText(s.value) : fmt(s.value, showQuality ? 0 : showForm ? fm.d : 2)}</td>
      <td>{Math.round(s.len).toLocaleString('sv-SE')} m</td>
    </tr>
  );
  // Top 10 / bottom 5 named streets of the shown measure (both street views).
  const streetTables = (topTitle, bottomTitle, col) => (
    <>
      {selectedStreet && (
        <button className="os-scenario-btn ss-clear" onClick={() => setSelectedStreet(null)} data-tip="ss.clearStreet">
          Visa alla gator (nu: {selectedStreet})
        </button>
      )}
      {top.length > 0 && (
        <>
          <h2>{topTitle}</h2>
          <table className="os-table">
            <thead><tr><th>#</th><th>Gata</th><th>{col}</th><th>Längd i rutan</th></tr></thead>
            <tbody>{top.map((s, i) => streetRow(s, i + 1))}</tbody>
          </table>
        </>
      )}
      {bottom.length > 0 && (
        <>
          <h2 className="os-parks-heading">{bottomTitle}</h2>
          <table className="os-table">
            <thead><tr><th>#</th><th>Gata</th><th>{col}</th><th>Längd i rutan</th></tr></thead>
            <tbody>{bottom.map((s, i) => streetRow(s, streets.length - i))}</tbody>
          </table>
        </>
      )}
    </>
  );

  return (
    <div className="ss-tool">
      <div className="os-controls">
        <button onClick={startSelection} className={selecting ? 'active-mode' : ''} disabled={isAnalyzing} data-tip="ss.select">
          {selecting ? 'Klicka på kartan…' : 'Välj område'}
        </button>
        <label className="os-checkbox" data-tip="ss.paths">
          <input type="checkbox" checked={includePaths} onChange={(e) => setIncludePaths(e.target.checked)} disabled={isAnalyzing} />
          Med gång- och cykelvägar
        </label>
        <label className="ss-group" data-tip="ss.group">
          Service för
          <select value={serviceGroup} onChange={(e) => setServiceGroup(e.target.value)} disabled={isAnalyzing}>
            {TARGET_GROUP_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        <button onClick={handleAnalyze} disabled={!bounds || isAnalyzing} className="os-analyze-btn" data-tip="ss.analyze">
          {isAnalyzing ? 'Analyserar…' : 'Analysera'}
        </button>
        <button onClick={exportPng} disabled={!result || isAnalyzing} className="os-export-btn" data-tip="ss.png">
          Spara som PNG
        </button>
        <button onClick={exportZip} disabled={!result || isAnalyzing || !!zipProgress} className="os-export-btn" data-tip="ss.zip">
          {zipProgress ? `Ritar karta ${zipProgress.done} av ${zipProgress.total}…` : 'Alla kartor (zip)'}
        </button>
        {result && attractMedian != null && (
          <span className="ss-attract" data-tip="ss.q.attractFig">
            <i style={{ background: SPECTRUM[qBand(QUALITY_MEASURES.attract, attractMedian)] }} />
            <span><small>Attraktivitet</small><strong>{fmt(attractMedian, 0)}</strong> av 100</span>
          </span>
        )}
        {bounds && (
          <span className={`os-area-info ${selAreaKm2 > MAX_AREA_KM2 ? 'os-too-big' : ''}`} data-tip="ss.area">
            Valt område: {selAreaKm2.toFixed(2).replace('.', ',')} km² (max {MAX_AREA_KM2} km²)
          </span>
        )}
      </div>

      {result && (hasUrbanitet || form || quality || hasReach || walk || hasPed) && (
        <div className="ss-view">
          <div className="os-scenario">
            <span className="os-scenario-label">Analys:</span>
            <button className={`os-scenario-btn ${showSyntax ? 'active' : ''}`}
                    onClick={() => switchView('syntax')} data-tip="ss.view.syntax">
              Space syntax
            </button>
            {hasPed && (
              <button className={`os-scenario-btn ${showPed ? 'active' : ''}`}
                      onClick={() => switchView('ped')} data-tip="ss.view.ped">
                Gångflöde
              </button>
            )}
            {form && (
              <button className={`os-scenario-btn ${showForm ? 'active' : ''}`}
                      onClick={() => switchView('form')} data-tip="ss.view.form">
                Gaturum
              </button>
            )}
            {quality && (
              <button className={`os-scenario-btn ${showQuality ? 'active' : ''}`}
                      onClick={() => switchView('quality')} data-tip="ss.view.quality">
                Stadskvaliteter
              </button>
            )}
            {hasReach && (
              <button className={`os-scenario-btn ${showReach ? 'active' : ''}`}
                      onClick={() => switchView('reach')} data-tip="ss.view.reach">
                Räckvidd
              </button>
            )}
            {walk && (
              <button className={`os-scenario-btn ${showWalk ? 'active' : ''}`}
                      onClick={() => switchView('walk')} data-tip="ss.view.walk">
                Gångvänlighet
              </button>
            )}
            {hasUrbanitet && (
              <button className={`os-scenario-btn ${showUrb ? 'active' : ''}`}
                      onClick={() => switchView('urbanitet')} data-tip="ss.view.urbanitet">
                Urbanitetstal
              </button>
            )}
          </div>
          {showUrb && (
            <div className="ss-ulegend" data-tip="ss.urb.legend">
              {U_CLASSES.map(c => <span key={c.label}><i style={{ background: c.color }} />{c.label}</span>)}
            </div>
          )}
        </div>
      )}

      {result && showForm && !form.error && (
        <div className="ss-view">
          <div className="os-scenario">
            <span className="os-scenario-label">Mått:</span>
            {Object.entries(FORM_MEASURES).map(([k, v]) => (
              <button key={k} className={`os-scenario-btn ${formKey === k ? 'active' : ''}`}
                      disabled={k === 'pressure' && form.pinchShare == null}
                      onClick={() => { setFormKey(k); setSelectedStreet(null); }} data-tip={`ss.form.${k}`}>
                {v.label}
              </button>
            ))}
          </div>
          <div className="ss-legend" data-tip="ss.form.legend">
            <span>{fm.low} &lt; {fmt(fm.classes[0], fm.d)}{fm.unit}</span>
            <span className="ss-legend-bar">
              {SPECTRUM.map((c, i) => <span key={c} style={{ background: c, height: 4 + i }} />)}
            </span>
            <span>{fm.high} ≥ {fmt(fm.classes[8], fm.d)}{fm.unit}</span>
          </div>
          {formKey !== 'sightM' && (
            <div className="ss-needs" data-tip="ss.form.open">
              <span><i style={{ background: OPEN_COLOR }} />öppet – hus på högst ena sidan</span>
            </div>
          )}
        </div>
      )}

      {result && showQuality && !quality.error && (
        <div className="ss-view">
          <div className="os-scenario">
            <span className="os-scenario-label">Mått:</span>
            {Object.entries(QUALITY_MEASURES).map(([k, v]) => (
              <button key={k} className={`os-scenario-btn ${qKey === k ? 'active' : ''}`}
                      disabled={!!v.blocks && !blockFeatures.length}
                      onClick={() => { setQKey(k); setSelectedStreet(null); setSelectedLink(null); setSelectedBlock(null); }}
                      data-tip={`ss.q.${k}`}>
                {v.label}
              </button>
            ))}
          </div>
          <div className="ss-legend" data-tip="ss.q.legend">
            <span>{qEnds(qm)[0]}</span>
            <span className="ss-legend-bar">
              {SPECTRUM.map((c, i) => <span key={c} style={{ background: c, height: 4 + i }} />)}
            </span>
            <span>{qEnds(qm)[1]}</span>
          </div>
          <div className="ss-needs">
            <span>{qm.legend}</span>
            {qKey === 'railM' && <span><i style={{ background: '#fff', borderColor: '#111' }} />station eller uppgång</span>}
            {(qKey === 'waterM' || qKey === 'railM') && <span><i style={{ background: OPEN_COLOR }} />{qm.none}</span>}
          </div>
        </div>
      )}

      {result && showReach && (
        <div className="ss-view">
          <div className="os-scenario">
            <span className="os-scenario-label">Gångtakt:</span>
            {PACES.map(p => (
              <button key={p.id} className={`os-scenario-btn ${paceId === p.id ? 'active' : ''}`}
                      onClick={() => { setPaceId(p.id); setSelectedStreet(null); }} data-tip={`ss.pace.${p.id}`}>
                {p.label} <span className="ss-walk">{String(p.ms).replace('.', ',')} m/s</span>
              </button>
            ))}
          </div>
          <div className="ss-legend" data-tip="ss.reach.legend">
            <span>Inget behov</span>
            <span className="ss-legend-bar">
              {SPECTRUM.map((c, i) => <span key={c} style={{ background: c, height: 4 + i }} />)}
            </span>
            <span>Alla {needs.length} behov</span>
          </div>
          <div className="ss-needs" data-tip="ss.serviceLegend">
            {needs.map(n => (
              <span key={n.id}><i style={{ background: needColor(n.id) }} />{n.label}</span>
            ))}
          </div>
        </div>
      )}

      {result && showPed && !ped.error && (
        <div className="ss-view">
          <div className="ss-legend" data-tip="ss.ped.legend">
            <span>Färre än {PED_CLASSES[0]}</span>
            <span className="ss-legend-bar">
              {SPECTRUM.map((c, i) => <span key={c} style={{ background: c, height: 4 + i }} />)}
            </span>
            <span>{PED_CLASSES[8].toLocaleString('sv-SE')} eller fler gående per vardag</span>
          </div>
        </div>
      )}

      {result && showWalk && !walk.error && (
        <div className="ss-view">
          <div className="ss-legend" data-tip="ss.walk.legend">
            <span>Inget vardagsmål</span>
            <span className="ss-legend-bar">
              {SPECTRUM.map((c, i) => <span key={c} style={{ background: c, height: 4 + i }} />)}
            </span>
            <span>Alla {walkKinds.length} slag inom 5 min</span>
          </div>
        </div>
      )}

      {result && showSyntax && (
        <div className="ss-view">
          <div className="os-scenario">
            <span className="os-scenario-label">Mått:</span>
            {Object.entries(MEASURES).map(([k, v]) => (
              <button key={k} className={`os-scenario-btn ${shownMeasure === k ? 'active' : ''}`}
                      disabled={k === 'nachB' && !hasBetween}
                      onClick={() => setMeasure(k)}
                      data-tip={k === 'nachB' && !hasBetween ? 'ss.measureOld' : `ss.measure.${k}`}>
                {v.label}
              </button>
            ))}
          </div>
          <div className="os-scenario">
            <span className="os-scenario-label">Radie:</span>
            {RADII.map(r => (
              <button key={r} className={`os-scenario-btn ${radius === r ? 'active' : ''}`}
                      onClick={() => setRadius(r)} data-tip="ss.radius">
                {RADIUS_LABELS[r]} <span className="ss-walk">≈ {WALK_MIN[r]} min</span>
              </button>
            ))}
          </div>
          <label className={`os-checkbox ss-weighted ${hasWeights ? '' : 'ss-disabled'}`}
                 data-tip={hasWeights ? 'ss.weighted' : 'ss.weightedOld'}>
            <input type="checkbox" checked={showWeighted} disabled={!hasWeights}
                   onChange={(e) => { setWeighted(e.target.checked); setSelectedStreet(null); }} />
            Viktad med {weightText}
          </label>
          <div className="ss-legend" data-tip="ss.legend">
            <span>{m.low}</span>
            <span className="ss-legend-bar">
              {SPECTRUM.map((c, i) => <span key={c} style={{ background: c, height: 4 + i }} />)}
            </span>
            <span>{m.high}</span>
          </div>
          {showWeighted && needs.length > 0 && (
            <div className="ss-needs" data-tip="ss.serviceLegend">
              {needs.map(n => (
                <span key={n.id}><i style={{ background: needColor(n.id) }} />{n.label}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <div className="os-error">{error}</div>}
      {status && (
        <div className={`os-status ${busy ? 'os-status-busy' : ''}`}>
          {busy && <span className="os-spinner" aria-hidden="true" />}
          <span>{status}</span>
          {busy && <span className="os-elapsed">{elapsed} s{busy.expect ? ` · ${busy.expect}` : ''}</span>}
        </div>
      )}

      <div className="os-map-container">
        <GoogleMap
          mapContainerClassName="os-map"
          center={initialView.center}
          zoom={initialView.zoom}
          onLoad={(map) => { mapRef.current = map; setMapReady(true); }}
          onClick={handleMapClick}
          onMouseMove={handleMouseMove}
          onCenterChanged={handleCenterChanged}
          options={{
            streetViewControl: false,
            mapTypeControl: true,
            mapTypeId: 'roadmap',
            styles: MAP_STYLES,
            clickableIcons: false,
            draggableCursor: selecting ? 'crosshair' : undefined,
          }}
        >
          {selBounds && (
            <Rectangle
              bounds={selBounds}
              onLoad={(r) => { rectRef.current = r; }}
              onUnmount={() => { rectRef.current = null; }}
              onBoundsChanged={handleRectBoundsChanged}
              options={{
                fillColor: '#2979FF',
                fillOpacity: result ? 0 : 0.06,
                strokeColor: '#2979FF',
                strokeWeight: 3,
                editable: !isAnalyzing && !!bounds,
                draggable: !isAnalyzing && !!bounds,
                clickable: false,
              }}
            />
          )}
          {selBounds && dimsLabel && (
            <Marker
              position={{ lat: selBounds.north, lng: (selBounds.west + selBounds.east) / 2 }}
              clickable={false}
              label={{ text: dimsLabel, color: '#2979FF', fontSize: '15px', fontWeight: 'bold', className: 'os-dims-label' }}
              icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 0 }}
            />
          )}
          {showUrb && selectedKvarter && (
            <InfoWindow position={selectedKvarter.pos} onCloseClick={() => setSelectedKvarter(null)}>
              <div className="os-infowindow ss-info">
                <strong>{kvarterTitle(selectedKvarter.props)}</strong>
                <div>Urbanitetstal: <b>{fmt(selectedKvarter.props.u)}</b></div>
                <div className="ss-factors">
                  Fastighetsgränser inne i kvarteret {Math.round(selectedKvarter.props.innerM).toLocaleString('sv-SE')} m
                  {' '}/ omkrets {Math.round(selectedKvarter.props.outerM).toLocaleString('sv-SE')} m
                </div>
                <div className="ss-info-foot">
                  {selectedKvarter.props.plots} {selectedKvarter.props.plots === 1 ? 'fastighet' : 'fastigheter'}
                  {' '}· {areaText(selectedKvarter.props.areaM2)}
                </div>
              </div>
            </InfoWindow>
          )}
          {selectedLink && showForm && (
            <InfoWindow position={selectedLink.pos} onCloseClick={() => setSelectedLink(null)}>
              <div className="os-infowindow ss-info">
                <strong>{streetName(selectedLink.props)}</strong>
                <table>
                  <tbody>
                    <tr className={formKey === 'sightM' ? 'ss-current' : ''}>
                      <td>Siktlängd</td>
                      <td>{selectedLink.props.sightM == null ? '–' : `${selectedLink.props.sightM.toLocaleString('sv-SE')} m`}</td>
                    </tr>
                    <tr className={formKey === 'widthM' ? 'ss-current' : ''}>
                      <td>Gatubredd, fasad till fasad</td>
                      <td>{selectedLink.props.widthM == null ? 'öppet' : `${fmt(selectedLink.props.widthM, 0)} m`}</td>
                    </tr>
                    <tr>
                      <td>Fasadhöjd</td>
                      <td>{selectedLink.props.facadeH == null ? '–' : `ca ${fmt(selectedLink.props.facadeH, 0)} m`}</td>
                    </tr>
                    <tr className={formKey === 'enclosure' ? 'ss-current' : ''}>
                      <td>Rumslighet, höjd/bredd</td>
                      <td>{selectedLink.props.enclosure == null ? '–' : fmt(selectedLink.props.enclosure)}</td>
                    </tr>
                    <tr>
                      <td>Hus på båda sidor</td>
                      <td>{selectedLink.props.wallShare == null ? '–' : `${fmt(100 * selectedLink.props.wallShare, 0)} %`}</td>
                    </tr>
                    {form.pinchShare != null && (
                      <tr className={formKey === 'pressure' ? 'ss-current' : ''}>
                        <td>Trängsel, genomströmning mot bredd</td>
                        <td>{selectedLink.props.pressure == null ? '–' : fmt(selectedLink.props.pressure)}</td>
                      </tr>
                    )}
                    {formWeighted && selectedLink.props.formF != null && (
                      <tr>
                        <td>Faktor i viktad space syntax</td>
                        <td>×{fmt(selectedLink.props.formF)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="ss-info-foot">Segment {Math.round(selectedLink.props.lengthM)} m</div>
              </div>
            </InfoWindow>
          )}
          {showBlocks && selectedBlock && (
            <InfoWindow position={selectedBlock.pos} onCloseClick={() => setSelectedBlock(null)}>
              <div className="os-infowindow ss-info">
                <strong>Kvarter</strong>
                <div>Bebyggd kvarterskant: <b>{fmt(100 * selectedBlock.props.edgeBuilt, 0)} %</b></div>
                <div className="ss-factors">
                  Andel av de yttersta 10 m mot gatan som är byggnad · hela kvarteret
                  {' '}{fmt(100 * selectedBlock.props.builtShare, 0)} % bebyggt
                </div>
                <div className="ss-info-foot">{areaText(selectedBlock.props.areaM2)}</div>
              </div>
            </InfoWindow>
          )}
          {selectedLink && showQuality && !showBlocks && (
            <InfoWindow position={selectedLink.pos} onCloseClick={() => setSelectedLink(null)}>
              <div className="os-infowindow ss-info">
                <strong>{streetName(selectedLink.props)}</strong>
                <table>
                  <tbody>
                    <tr className={qKey === 'attract' ? 'ss-current' : ''}>
                      <td>Attraktivitet</td>
                      <td>{attractScore(selectedLink.props) == null ? '–'
                        : `${fmt(attractScore(selectedLink.props), 0)} av 100`}</td>
                    </tr>
                    <tr className={qKey === 'waterM' ? 'ss-current' : ''}>
                      <td>Vatten, gångväg</td>
                      <td>{selectedLink.props.waterM == null ? 'inget inom 3 km'
                        : `${selectedLink.props.waterEst ? 'ca ' : ''}${selectedLink.props.waterM.toLocaleString('sv-SE')} m`}</td>
                    </tr>
                    <tr className={qKey === 'bizN' ? 'ss-current' : ''}>
                      <td>Verksamheter inom 1 km</td>
                      <td>{selectedLink.props.bizN == null ? '–'
                        : `${selectedLink.props.bizN.toLocaleString('sv-SE')} (${selectedLink.props.bizTypes} slag)`}</td>
                    </tr>
                    <tr className={qKey === 'railM' ? 'ss-current' : ''}>
                      <td>Spårstation, gångväg</td>
                      <td>{selectedLink.props.railM == null ? 'ingen inom 3 km'
                        : `${selectedLink.props.railEst ? 'ca ' : ''}${selectedLink.props.railM.toLocaleString('sv-SE')} m`
                          + ` (${RAIL_MODES[selectedLink.props.railMode] || 'spår'})`}</td>
                    </tr>
                    <tr className={qKey === 'barrierM' ? 'ss-current' : ''}>
                      <td>Motorväg</td>
                      <td>{selectedLink.props.motorwayM == null ? 'över 1 km' : `${selectedLink.props.motorwayM.toLocaleString('sv-SE')} m`}</td>
                    </tr>
                    <tr className={qKey === 'barrierM' ? 'ss-current' : ''}>
                      <td>Järnväg</td>
                      <td>{selectedLink.props.railwayM == null ? 'över 1 km' : `${selectedLink.props.railwayM.toLocaleString('sv-SE')} m`}</td>
                    </tr>
                  </tbody>
                </table>
                {(selectedLink.props.waterEst || selectedLink.props.railEst) && (
                  <div className="ss-info-foot">ca = utanför det modellerade gatunätet, fågelvägen × 1,3</div>
                )}
                <div className="ss-info-foot">Segment {Math.round(selectedLink.props.lengthM)} m</div>
              </div>
            </InfoWindow>
          )}
          {selectedLink && showReach && (
            <InfoWindow position={selectedLink.pos} onCloseClick={() => setSelectedLink(null)}>
              <div className="os-infowindow ss-info">
                <strong>{streetName(selectedLink.props)}</strong>
                <div>
                  {reachMet(selectedLink.props, needs, pace).length} av {needs.length} behov
                  {' '}inom räckhåll i {pace.label.toLowerCase()} ({String(pace.ms).replace('.', ',')} m/s)
                </div>
                <table>
                  <thead><tr><th>Behov</th><th>Avstånd</th><th>Räcker till</th></tr></thead>
                  <tbody>
                    {needs.map((n, k) => {
                      const d = selectedLink.props.svcD?.[k];
                      const ok = d != null && d <= paceReach(n, pace);
                      return (
                        <tr key={n.id} className={ok ? 'ss-met' : 'ss-unmet'}>
                          <td>{ok ? '✓' : '✗'} {n.label}</td>
                          <td>{d == null ? `över ${n.maxMeters.toLocaleString('sv-SE')} m` : `${d.toLocaleString('sv-SE')} m`}</td>
                          <td>{Math.round(paceReach(n, pace)).toLocaleString('sv-SE')} m</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="ss-info-foot">
                  Gångväg längs gatunätet{needs.some(n => n.crowFlies) ? ' (grönyta fågelvägen)' : ''} · segment {Math.round(selectedLink.props.lengthM)} m
                </div>
              </div>
            </InfoWindow>
          )}
          {showWalk && selectedBuilding && (
            <InfoWindow position={selectedBuilding.pos} onCloseClick={() => setSelectedBuilding(null)}>
              <div className="os-infowindow ss-info">
                <strong>{selectedBuilding.props.n} av {walkKinds.length} slags vardagsmål inom 5 minuter</strong>
                <ul>
                  {walkKinds.map((kd, k) => {
                    const ok = (selectedBuilding.props.mask >> k) & 1;
                    return (
                      <li key={kd.id} className={ok ? 'ss-met' : 'ss-unmet'}>{ok ? '✓' : '✗'} {kd.label}</li>
                    );
                  })}
                </ul>
                <div className="ss-info-foot">400 m gångväg från husets närmaste gata</div>
              </div>
            </InfoWindow>
          )}
          {selectedLink && showPed && (
            <InfoWindow position={selectedLink.pos} onCloseClick={() => setSelectedLink(null)}>
              <div className="os-infowindow ss-info">
                <strong>{streetName(selectedLink.props)}</strong>
                <div>Gångflöde: <b>{pedText(selectedLink.props.ped)}</b> gående per vardag (07–22)</div>
                {selectedLink.props.ped != null && (
                  <table>
                    <thead><tr><th>Uppskattningen bygger på</th><th /></tr></thead>
                    <tbody>
                      <tr><td>Enklaste vägar som passerar, inom 2 km</td><td>{selectedLink.props.pedBet.toLocaleString('sv-SE')}</td></tr>
                      <tr><td>Butiker, service, restauranger inom 500 m</td><td>{selectedLink.props.pedShops}</td></tr>
                      <tr><td>Fastigheter inom 500 m</td><td>{selectedLink.props.pedPlots}</td></tr>
                      <tr><td>Hastighetsgräns</td><td>{speedText(selectedLink.props.speed)}</td></tr>
                      <tr><td>Segmentets längd</td><td>{Math.round(selectedLink.props.lengthM)} m</td></tr>
                    </tbody>
                  </table>
                )}
                <div className="ss-info-foot">Chalmers Stockholmsmodell – rangordningen säkrare än antalet</div>
              </div>
            </InfoWindow>
          )}
          {selectedLink && showSyntax && (
            <InfoWindow position={selectedLink.pos} onCloseClick={() => setSelectedLink(null)}>
              <div className="os-infowindow ss-info">
                <strong>{streetName(selectedLink.props)}</strong>
                <div>
                  {showWeighted ? 'Viktad ' + m.name.toLowerCase() : m.name} {RADIUS_LABELS[radius]}:
                  {' '}{fmt(selectedLink.props[key])} – {rankText(selectedLink.props[key])}
                </div>
                {hasWeights && (
                  <div className="ss-factors">
                    Hastighet {speedText(selectedLink.props.speed)} → ×{fmt(selectedLink.props.speedF)}
                    {selectedLink.props.svcShare != null && (
                      <> · Service {(selectedLink.props.svc || []).length} av {needs.length} behov
                        {' '}→ ×{fmt(0.5 + selectedLink.props.svcShare)}</>
                    )}
                    {formWeighted && selectedLink.props.formF != null && (
                      <> · Gaturum {selectedLink.props.enclosure != null ? `H/B ${fmt(selectedLink.props.enclosure)}`
                        : PATH_HW.has(selectedLink.props.hw) ? 'gångväg' : selectedLink.props.widthM == null ? 'öppet' : 'höjd okänd'}
                        {' '}→ ×{fmt(selectedLink.props.formF)}</>
                    )}
                    {selectedLink.props.svcShare != null && (
                      <ul>
                        {needs.map(n => (
                          <li key={n.id} className={(selectedLink.props.svc || []).includes(n.id) ? 'ss-met' : 'ss-unmet'}>
                            {(selectedLink.props.svc || []).includes(n.id) ? '✓' : '✗'} {n.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <table>
                  <thead>
                    <tr>
                      <th>Radie</th><th>Integration</th>{hasWeights && <th>viktad</th>}
                      <th>Genomströmning</th>{hasWeights && <th>viktad</th>}
                      {hasBetween && <th>Mellan husen</th>}{hasBetween && hasWeights && <th>viktad</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {RADII.map(r => (
                      <tr key={r} className={r === radius ? 'ss-current' : ''}>
                        <td>{RADIUS_LABELS[r]}</td>
                        <td>{fmt(selectedLink.props[`nain${r}`])}</td>
                        {hasWeights && <td>{fmt(selectedLink.props[`nainW${r}`])}</td>}
                        <td>{fmt(selectedLink.props[`nach${r}`])}</td>
                        {hasWeights && <td>{fmt(selectedLink.props[`nachW${r}`])}</td>}
                        {hasBetween && <td>{fmt(selectedLink.props[`nachB${r}`])}</td>}
                        {hasBetween && hasWeights && <td>{fmt(selectedLink.props[`nachBW${r}`])}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="ss-info-foot">
                  Segment {Math.round(selectedLink.props.lengthM)} m · {selectedLink.props.conn} anslutande segment
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>

      {result && showUrb && (
        <div className="os-results">
          {urbSummary.error ? (
            <p className="os-disclaimer">Urbanitetstalet kunde inte beräknas: {urbSummary.error}</p>
          ) : (
            <dl className="os-metrics">
              <div data-tip="ss.urb.u">
                <dt>Urbanitetstal</dt>
                <dd>{fmt(urbSummary.u)}<small>för området: alla inre fastighetsgränser / alla kvarters omkrets</small></dd>
              </div>
              <div data-tip="ss.urb.median">
                <dt>Median</dt>
                <dd>{fmt(urbSummary.uMedian)}<small>kvarterens urbanitetstal</small></dd>
              </div>
              <div data-tip="ss.urb.share">
                <dt>1 eller mer</dt>
                <dd>{fmt(100 * (urbSummary.urbanShare || 0), 0)} %<small>av kvartersytan</small></dd>
              </div>
              <div data-tip="ss.urb.count">
                <dt>Kvarter</dt>
                <dd>
                  {(urbSummary.kvarterCount || 0).toLocaleString('sv-SE')}
                  <small>{(urbSummary.plots || 0).toLocaleString('sv-SE')} fastigheter</small>
                </dd>
              </div>
            </dl>
          )}
          {kvarterRanked.length > 0 && (
            <>
              <h2>Högst urbanitetstal</h2>
              <table className="os-table">
                <thead><tr><th>#</th><th>Kvarter</th><th>Tal</th><th>Fastigheter</th><th>Yta</th></tr></thead>
                <tbody>
                  {kvarterRanked.slice(0, 10).map((f, i) => (
                    <tr key={f.properties.kid} onClick={() => focusKvarter(f)} data-tip="ss.urb.row"
                        className={selectedKvarter?.props?.kid === f.properties.kid ? 'os-row-selected' : ''}>
                      <td>{i + 1}</td>
                      <td><span className="ss-swatch" style={{ background: uColor(f.properties.u) }} />{kvarterTitle(f.properties)}</td>
                      <td>{fmt(f.properties.u)}</td>
                      <td>{f.properties.plots}</td>
                      <td>{areaText(f.properties.areaM2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          <details className="ss-explain">
            <summary data-tip="ss.urb.explain">Så räknas urbanitetstalet</summary>
            <p>
              <strong>Urbanitetstalet</strong> (Mikael Askergren) mäter hur finmaskigt ett kvarter är
              uppdelat i fastigheter: längden av fastighetsgränserna inne i kvarteret delad med
              kvarterets omkrets. <strong>0</strong> betyder att hela kvarteret är en fastighet.
              <strong> 1 eller mer</strong> betyder att gränserna inne i kvarteret är minst lika
              långa som kvarterets kant – typiskt för den täta stadens kvarter. Enligt teorin ger
              många fastigheter fler entréer och verksamheter längs gatan och fler skäl att gå.
            </p>
            <p>
              <strong>Kvarteret</strong> är sammanhängande bebyggd mark: en fastighet räknas till
              kvarteret när minst 5 % av den är bebyggd. Gator, parker, gångstråk och vatten skiljer
              kvarteren åt – i ett storgårdsområde blir därför varje bostadsfastighet ofta ett eget
              kvarter. En gata som går över en stor fastighet delar den. Obebyggda fickor inne i ett
              kvarter (till exempel en gemensam gård) räknas till kvarteret. Gränser mellan två delar
              av samma fastighet räknas inte.
            </p>
            <p className="os-disclaimer">
              Fastighetsindelningen visar fastigheter, inte ägare: två fastigheter med samma ägare
              räknas som två. Kvarter som sträcker sig mer än 400 m utanför rutan räknas inte.
              Underlaget är Lantmäteriets fastighetsindelning och byggnader, utlämnade för test i
              forskningsprojektet – kartan visar bara de härledda kvarteren, inte fastighetsgränser
              eller fastighetsbeteckningar.
            </p>
          </details>
          <p className="os-disclaimer os-attribution">{URB_ATTRIBUTION} · Bakgrundskarta © Google</p>
        </div>
      )}

      {result && showForm && (
        <div className="os-results">
          {form.error ? (
            <p className="os-disclaimer">Gaturummen kunde inte mätas: {form.error}</p>
          ) : (
            <dl className="os-metrics">
              <div data-tip="ss.form.sight">
                <dt>Siktlängd</dt>
                <dd>{form.sightMedian == null ? '–' : `${form.sightMedian.toLocaleString('sv-SE')} m`}<small>median längs gatorna i rutan</small></dd>
              </div>
              <div data-tip="ss.form.width">
                <dt>Gatubredd</dt>
                <dd>{form.widthMedian == null ? '–' : `${fmt(form.widthMedian, 0)} m`}<small>median fasad till fasad</small></dd>
              </div>
              <div data-tip="ss.form.encl">
                <dt>Rumslighet</dt>
                <dd>
                  {fmt(form.enclosureMedian)}
                  <small>median höjd/bredd{form.facadeHMedian != null && `, fasader ca ${fmt(form.facadeHMedian, 0)} m`}</small>
                </dd>
              </div>
              <div data-tip="ss.form.wall">
                <dt>Hus på båda sidor</dt>
                <dd>{fmt(100 * (form.wallShare || 0), 0)} %<small>av gatulängden</small></dd>
              </div>
              {form.pinchShare != null && (
                <div data-tip="ss.form.pinch">
                  <dt>Flaskhalsar</dt>
                  <dd>{fmt(100 * form.pinchShare, 0)} %<small>av gatulängden med trängsel 1 eller mer</small></dd>
                </div>
              )}
            </dl>
          )}
          {!form.error && form.heightKnownShare === 0 && (
            <p className="os-disclaimer">Byggnadshöjder (NMD) saknas för området – rumsligheten kan inte beräknas.</p>
          )}
          {!form.error && streetTables(`${fm.top}`, `${fm.bottom}`,
            formKey === 'enclosure' ? 'H/B' : formKey === 'pressure' ? 'Index' : 'Meter')}
          <details className="ss-explain">
            <summary data-tip="ss.form.explain">Så mäts gaturummen</summary>
            <p>
              Längs varje gata mäts var 10:e meter, mot Lantmäteriets byggnader:
            </p>
            <p>
              <strong>Siktlängd</strong> – hur långt man ser längs gatan innan ett hus skymmer, framåt
              och bakåt tillsammans (högst 500 m åt varje håll). En rak gata mellan två fasader har
              samma siktlängd hela vägen; en krokig gränd kort. Lång sikt gör stråket lätt att hitta
              och se; kort sikt ger ett slutet, intimt gaturum.
            </p>
            <p>
              <strong>Gatubredd</strong> – avståndet mellan fasaderna tvärs gatan, där det finns hus
              inom 40 m på båda sidor.
            </p>
            <p>
              <strong>Rumslighet</strong> – fasadernas höjd delad med gatubredden (H/B). Enligt
              stadsbyggnadens tumregel ger 1–0,33 (höjd 1:1 till 1:3 mot bredden) ett tydligt gaturum;
              under ungefär 0,25 försvinner känslan av rum. Höjden är husets klass i
              Naturvårdsverkets Nationella Marktäckedata (laserskanning, 5–45 m); hus under 5 m räknas
              som 4 m.
            </p>
            <p>
              <strong>Trängsel</strong> – hur mycket genomgående rörelse gatan har i förhållande till
              sitt gaturum: gatans rang i genomströmning på 800 m inom rutan (0–1) gånger 15 m delat
              med gatubredden. 1 betyder rutans största stråk med 15 m mellan fasaderna; ett stråk i
              översta tredjedelen på 10 m bredd hamnar också nära 1. Där är risken störst för
              trängsel och konflikter mellan gående, cyklister och bilar – Space Syntax kallar det
              stråkets kapacitet för aktiv mobilitet.
            </p>
            <p className="os-disclaimer">
              Bredd, rumslighet och trängsel visas bara där husen kantar båda sidor längs minst en
              fjärdedel av segmentet (grått annars). Bredden är fasad till fasad, inte trottoarens
              bredd, och genomströmningen är gatunätets form, inte räknade flöden – trängseln visar var
              det är värt att titta närmare. Träd, plank och höjdskillnader i marken räknas inte. Nyare
              hus kan saknas i höjddatan. Gångvägar ingår på kartan men inte i medianerna och listorna.
            </p>
          </details>
          <p className="os-disclaimer os-attribution">{FORM_ATTRIBUTION} · Bakgrundskarta © Google</p>
        </div>
      )}

      {result && showQuality && (
        <div className="os-results">
          {quality.error ? (
            <p className="os-disclaimer">Stadskvaliteterna kunde inte beräknas: {quality.error}</p>
          ) : (
            <dl className="os-metrics">
              <div data-tip="ss.q.attractFig">
                <dt>Attraktivitet</dt>
                <dd>
                  {attractMedian == null ? '–' : fmt(attractMedian, 0)}
                  <small>av 100, median längs gatorna · Östermalm och Södermalm ≈ 60, Rinkeby ≈ 10</small>
                </dd>
              </div>
              <div data-tip="ss.q.water">
                <dt>Vatten</dt>
                <dd>
                  {quality.water.medianM == null ? 'över 3 km' : `${quality.water.medianM.toLocaleString('sv-SE')} m`}
                  <small>median gångavstånd · {fmt(100 * quality.water.nearShare, 0)} % av gatorna inom 300 m</small>
                </dd>
              </div>
              <div data-tip="ss.q.biz">
                <dt>Stadsutbud</dt>
                <dd>
                  {quality.biz.medianN == null ? '–' : quality.biz.medianN.toLocaleString('sv-SE')}
                  <small>verksamheter inom 1 km, median{quality.biz.medianTypes != null && ` (${quality.biz.medianTypes} slag)`}</small>
                </dd>
              </div>
              <div data-tip="ss.q.rail">
                <dt>Spårstation</dt>
                <dd>{fmt(100 * quality.rail.nearShare, 0)} %<small>av gatulängden inom 500 m gångväg</small></dd>
              </div>
              <div data-tip="ss.q.barrier">
                <dt>Barriärer</dt>
                <dd>
                  {fmt(100 * quality.barrier.motorwayShare, 0)} % / {fmt(100 * quality.barrier.railwayShare, 0)} %
                  <small>av gatulängden inom 300 m från motorväg / 100 m från järnväg</small>
                </dd>
              </div>
              {quality.blocks && !quality.blocks.error && (
                <div data-tip="ss.q.blocks">
                  <dt>Kvartersform</dt>
                  <dd>
                    {quality.blocks.edgeBuilt == null ? '–' : `${fmt(100 * quality.blocks.edgeBuilt, 0)} %`}
                    <small>bebyggd kvarterskant i {quality.blocks.count.toLocaleString('sv-SE')} kvarter</small>
                  </dd>
                </div>
              )}
            </dl>
          )}
          {!quality.error && quality.blocks?.error && (
            <p className="os-disclaimer">Kvartersformen kunde inte beräknas: {quality.blocks.error}</p>
          )}
          {!quality.error && quality.biz?.missingShare > 0 && (
            <p className="os-disclaimer">Tiden räckte inte till stadsutbudet för alla gator – grått saknar värde.</p>
          )}
          {!quality.error && !showBlocks && streetTables(qm.top, qm.bottom, qm.col)}
          <details className="ss-explain">
            <summary data-tip="ss.q.explain">Vad bostadspriserna säger</summary>
            <p>
              Stadskvaliteterna är det som bostadspriserna visat sig betala för, utöver det som
              hälsomåtten redan fångar: Spacescape och Evidens värderingsstudier i Stockholm (2011)
              och Göteborg (2016), där ett fåtal stadskvaliteter förklarar omkring 90 % av
              prisskillnaderna mellan områden, och Köpenhamns universitets studie (2013).
            </p>
            <p>
              <strong>Attraktivitet</strong> – en poäng 0–100 av de två stadskvaliteter som bäst
              skilde de mest eftertraktade stadsdelarna från de minst eftertraktade i en jämförelse
              i oktober 2026: hur många slags verksamheter som finns inom 1 km gångväg och
              gångavståndet till vatten. Båda räknas om till sin percentil bland de bebyggda rutorna
              (150 × 150 m) på Östermalm, Södermalm och Rinkeby, och poängen är medelvärdet. Den
              placerade varje ruta rätt, även i Vasastan, Gamla stan och Tensta som inte användes
              för att ta fram den. Medianer längs gatorna: Gamla stan 65, Östermalm och Södermalm
              60, Vasastan 51, Rinkeby 10, Tensta 0. Poängen är kalibrerad i Stockholm och skiljer
              innerstad från Järva; för mellanområden och andra städer är den inte prövad, och
              vikterna är inte skattade mot priser.
            </p>
            <p>
              <strong>Vatten</strong> – gångavstånd längs gatunätet till hav, sjö eller å på minst
              5 hektar. Stockholm: 1 km närmare vatten ≈ +1 550 kr/kvm. Göteborg: inom 50 m från
              strandlinjen ≈ +7 800 kr/kvm. Danska kusten: +15–30 %, borta efter ungefär 300 m.
            </p>
            <p>
              <strong>Stadsutbud</strong> – restauranger, kaféer, barer, butiker (utom dagligvaror),
              kommersiell service och kultur inom 1 km gångväg. Stockholm: dubbelt så många ≈
              +1 760 kr/kvm, den största enskilda stadskvaliteten. Göteborg: ett slag till inom
              500 m ≈ +1 100 kr/kvm. Men en bar eller ett kafé inom 100 m från bostaden sänker
              villapriset 1–4 % (Danmark).
            </p>
            <p>
              <strong>Spårstation</strong> – gångavstånd till närmaste station eller uppgång för
              tåg, tunnelbana, lokalbana eller spårvagn. Stockholm: under 500 m ≈ +1 370 kr/kvm.
              Busshållplatser räknas inte: Stockholmsstudiens premie gäller spårstationer.
            </p>
            <p>
              <strong>Barriärer</strong> – avstånd fågelvägen till motorväg och järnväg ovan jord.
              Danmark: −7–10 % intill motorväg, borta efter 300–400 m; −10–15 % intill järnväg,
              borta efter ungefär 100 m. Buller mäts inte: avståndet är det som går att mäta i
              hela landet.
            </p>
            <p>
              <strong>Kvartersform</strong> – hur stor del av kvarterets yttersta 10 m mot gatan som
              är bebyggd: första halvan av Göteborgs kvartersstadsindex (+950 kr/kvm per 10
              enheter; Stockholm +500 kr/kvm). Andra halvan, andelen entréer mot gatan, kommer när
              Lantmäteriets adresspunkter finns. Ett kvarter är marken mellan gator, järnvägar och
              motorvägar; där fasader kantar gatan går kvarterets kant vid fasaden.
            </p>
            <p className="os-disclaimer">
              Kronbeloppen är samband från bostadsrättsförsäljningar 2010–2015 i Stockholm och
              Göteborg, inte prognoser, och gäller bäst i sammanhängande stad. Gångavstånd utanför
              det modellerade gatunätet (rutan + 1,2 km) uppskattas fågelvägen × 1,3. Vatten,
              verksamheter, stationer och vägar kommer från OpenStreetMap och kan sakna nyare eller
              omärkta verksamheter. Kvarter större än 30 hektar eller som fortsätter långt utanför
              rutan visas inte.
            </p>
          </details>
          <p className="os-disclaimer os-attribution">
            {QUALITY_ATTRIBUTION} · Bakgrundskarta © Google
            {quality.osmTimestamp && ` · OpenStreetMap ${new Date(quality.osmTimestamp).toLocaleDateString('sv-SE')}`}
          </p>
        </div>
      )}

      {result && showReach && reachStats && (
        <div className="os-results">
          <dl className="os-metrics">
            <div data-tip="ss.reach.all">
              <dt>Alla behov</dt>
              <dd>{fmt(100 * paceStats.all, 0)} %<small>av gatulängden, {pace.label.toLowerCase()}</small></dd>
            </div>
            <div data-tip="ss.reach.mean">
              <dt>I genomsnitt</dt>
              <dd>{fmt(100 * paceStats.mean, 0)} %<small>av behoven inom räckhåll</small></dd>
            </div>
            <div data-tip="ss.reach.group">
              <dt>Målgrupp</dt>
              <dd>{summary.weighting.serviceGroupLabel}<small>{needs.length} vardagsbehov från Promenadavstånd</small></dd>
            </div>
          </dl>
          <h2>Inom räckhåll i olika gångtakt</h2>
          <table className="os-table ss-reach-table">
            <thead>
              <tr>
                <th>Behov</th>
                {reachStats.map(r => (
                  <th key={r.pace.id} className={r.pace.id === pace.id ? 'ss-current' : ''}>
                    {r.pace.label}<small>{String(r.pace.ms).replace('.', ',')} m/s</small>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {needs.map((n, k) => (
                <tr key={n.id}>
                  <td><span className="ss-swatch" style={{ background: needColor(n.id) }} />{n.label}</td>
                  {reachStats.map(r => (
                    <td key={r.pace.id} className={r.pace.id === pace.id ? 'ss-current' : ''}>
                      {fmt(100 * r.perNeed[k], 0)} %<small>≤ {Math.round(paceReach(n, r.pace)).toLocaleString('sv-SE')} m</small>
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="ss-total">
                <td>Alla behov</td>
                {reachStats.map(r => (
                  <td key={r.pace.id} className={r.pace.id === pace.id ? 'ss-current' : ''}>{fmt(100 * r.all, 0)} %</td>
                ))}
              </tr>
            </tbody>
          </table>
          <p className="os-disclaimer">Andel av gatulängden i rutan (gångvägar ej medräknade) där behovet nås.</p>
          {summary.weighting?.serviceCapped && (
            <p className="os-disclaimer">Servicesökningen nådde taket för antal anrop – i de tätaste delarna kan platser saknas.</p>
          )}
          <details className="ss-explain">
            <summary data-tip="ss.reach.explain">Så räknas räckvidden</summary>
            <p>
              Räckvidden visar staden med olika personers ögon: samma vardagsbehov som i
              Promenadavstånd (målgruppen väljs före analysen), men för den som går långsammare.
              Avstånden i Promenadavstånd gäller vanlig gångtakt, ungefär 1,4 m/s. Den som går
              1,0 m/s når på samma tid 70 % så långt, och med rollator (0,7 m/s) hälften så långt –
              en livsmedelsbutik på 800 m blir då 400 m.
            </p>
            <p>
              Kartan färgar varje gata efter hur stor andel av behoven som nås därifrån i den valda
              takten: rött = alla, blått = inget. Avstånden går längs gatunätet till närmaste plats
              från Google; grönyta inom 300 m mäts fågelvägen, som i Promenadavstånd.
            </p>
            <p className="os-disclaimer">
              Metoden liknar Space Syntax CityZen Explorer, som visar hur väl staden fungerar för
              äldre med olika hälsa. Trappor, backar, bänkar och hur jämn gångbanan är räknas inte.
            </p>
          </details>
          <p className="os-disclaimer os-attribution">{ATTRIBUTION} · Platser © Google</p>
        </div>
      )}

      {result && showWalk && (
        <div className="os-results">
          {walk.error ? (
            <p className="os-disclaimer">Gångvänligheten kunde inte beräknas: {walk.error}</p>
          ) : (
            <>
              <dl className="os-metrics">
                <div data-tip="ss.walk.median">
                  <dt>Median</dt>
                  <dd>{fmt(walk.median, 0)} av {walk.kindCount}<small>slags vardagsmål inom 5 minuter</small></dd>
                </div>
                <div data-tip="ss.walk.half">
                  <dt>Minst {walk.half} slag</dt>
                  <dd>{fmt(100 * (walk.halfShare || 0), 0)} %<small>av husen</small></dd>
                </div>
                <div data-tip="ss.walk.count">
                  <dt>Hus</dt>
                  <dd>{(walk.buildings || 0).toLocaleString('sv-SE')}<small>med mittpunkt i rutan, minst {walk.minFootprintM2} m²</small></dd>
                </div>
              </dl>
              <h2>Vardagsmål inom 5 minuter</h2>
              <table className="os-table">
                <thead><tr><th>Slag</th><th>Andel av husen</th><th>Källa</th></tr></thead>
                <tbody>
                  {walkRanked.map(k => (
                    <tr key={k.id}>
                      <td>{k.label}</td>
                      <td>{k.share == null ? '–' : `${fmt(100 * k.share, 0)} %`}</td>
                      <td>{k.src === 'osm' ? 'OpenStreetMap' : 'Google'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!walk.osm && (
                <p className="os-disclaimer">Verksamheterna från OpenStreetMap saknas här – bara målgruppens behov räknas.</p>
              )}
              {!walk.serviceKnown && (
                <p className="os-disclaimer">Målgruppens platser kunde inte hämtas – bara verksamheterna räknas.</p>
              )}
              {!walk.complete && (
                <p className="os-disclaimer">Tiden räckte inte till alla hus – välj en mindre ruta för hela området.</p>
              )}
            </>
          )}
          <details className="ss-explain">
            <summary data-tip="ss.walk.explain">Så räknas gångvänligheten</summary>
            <p>
              Gångvänligheten följer Space Syntax Walkability Index: för varje hus räknas hur många
              <em> olika slags</em> vardagsmål som finns inom fem minuters promenad (400 m längs
              gatunätet). Många slag nära bostaden gör att fler ärenden blir till fots; få slag gör
              bilen till normen. Det är blandningen som räknas, inte antalet butiker.
            </p>
            <p>
              Slagen är målgruppens vardagsbehov (samma som i viktningen och Räckvidd, platser från
              Google) och fyra slags stadsverksamheter från OpenStreetMap: restauranger och kaféer,
              butiker, service och kultur. Rött = alla slag inom fem minuter, blått = inget.
            </p>
            <p className="os-disclaimer">
              Avståndet räknas från husets närmaste gata, inte från entrén. Hus under 30 m² (förråd,
              garage) räknas inte. Verksamheter som saknas i OpenStreetMap eller Google syns inte.
              Byggnaderna är Lantmäteriets.
            </p>
          </details>
          <p className="os-disclaimer os-attribution">{WALK_ATTRIBUTION} · Bakgrundskarta © Google</p>
        </div>
      )}

      {result && showPed && (
        <div className="os-results">
          {ped.error ? (
            <p className="os-disclaimer">Gångflödet kunde inte uppskattas: {ped.error}</p>
          ) : (
            <dl className="os-metrics">
              <div data-tip="ss.ped.median">
                <dt>Median</dt>
                <dd>{pedText(ped.medianDay)}<small>gående per vardag längs gatorna i rutan</small></dd>
              </div>
              <div data-tip="ss.ped.p90">
                <dt>Huvudstråken</dt>
                <dd>{pedText(ped.p90Day)}<small>eller fler på den tiondel av gatorna som har flest</small></dd>
              </div>
              <div data-tip="ss.ped.max">
                <dt>Högst</dt>
                <dd>{pedText(ped.maxDay)}<small>gående per vardag på ett gatusegment</small></dd>
              </div>
              <div data-tip="ss.ped.model">
                <dt>Modell</dt>
                <dd>STHLM Central<small>Chalmers, Stavroulaki m.fl. 2024</small></dd>
              </div>
            </dl>
          )}
          {!ped.error && ped.betSampleShare < 1 && (
            <p className="os-disclaimer">
              Genomströmningen inom 2 km räknades på ett urval av {fmt(100 * ped.betSampleShare, 0)} % av
              {' '}startpunkterna för att hinna klart – värdena är en skattning.
            </p>
          )}
          {!ped.error && streetTables('Flest gående', 'Minst gående', 'Per vardag')}
          <details className="ss-explain">
            <summary data-tip="ss.ped.explain">Så uppskattas gångflödet</summary>
            <p>
              Gångflödet är en prognos av hur många som går på varje gata en vanlig vardag mellan
              7 och 22. Modellen kommer från Chalmers (Stavroulaki, Ivarsson, Berghauser Pont och
              Verendel, Space Syntax Symposium 2024). Den är anpassad till heldagsmätningar av gående
              på 121 gator i Stockholms innerstad 2017 och prövad på 75 gator i Göteborgs centrum,
              där den förklarade ungefär hälften av skillnaderna (R² 0,52).
            </p>
            <p>
              Fem egenskaper hos varje gata ger uppskattningen: <strong>genomströmningen inom 2 km</strong>
              {' '}(fler gående på gator som ligger på många enkla vägar), <strong>butiker, service,
              restauranger och kaféer inom 500 m</strong> gångväg (fler), <strong>fastigheter inom
              500 m</strong> (något färre), <strong>hastighetsgränsen</strong> (fler på stadens
              flerfiliga huvudgator än på 30-gator och gågator) och <strong>segmentets längd</strong>
              {' '}(färre på långa segment mellan korsningarna, det vill säga kring stora kvarter).
            </p>
            <p className="os-disclaimer">
              Lita mer på rangordningen än på antalet: modellens mätningar var mobiler som fångades av
              wifi-sensorer, och felet var i Göteborg ungefär hälften av det typiska flödet. Modellen
              underskattar de mest kommersiella gatorna, där butikerna själva drar folk, och den är
              inte prövad i förorter eller små städer. Verksamheterna kommer från OpenStreetMap
              (utan livsmedelsbutiker), fastigheterna från Lantmäteriets fastighetsindelning – därför
              bara för utvalda testare. Gångvägar ingår på kartan men inte i medianen och listorna.
            </p>
          </details>
          <p className="os-disclaimer os-attribution">{PED_ATTRIBUTION} · Bakgrundskarta © Google</p>
        </div>
      )}

      {result && summary && showSyntax && (
        <div className="os-results">
          <dl className="os-metrics">
            <div data-tip="ss.meanNain">
              <dt>{showWeighted ? 'Viktad integration' : 'Integration'} {RADIUS_LABELS[radius]}</dt>
              <dd>{fmt(summary[`nain${showWeighted ? 'W' : ''}${radius}Mean`])}<small>medel för gatunätet i rutan (NAIN, längdviktat)</small></dd>
            </div>
            <div data-tip="ss.meanNach">
              <dt>{showWeighted ? 'Viktad genomströmning' : 'Genomströmning'} {RADIUS_LABELS[radius]}</dt>
              <dd>{fmt(summary[`nach${showWeighted ? 'W' : ''}${radius}Mean`])}<small>medel för gatunätet i rutan (NACH, längdviktat)</small></dd>
            </div>
            {hasBetween && (
              <div data-tip="ss.meanNachB">
                <dt>{showWeighted ? 'Viktad mellan husen' : 'Mellan husen'} {RADIUS_LABELS[radius]}</dt>
                <dd>{fmt(summary[`nachB${showWeighted ? 'W' : ''}${radius}Mean`])}<small>genomströmning mellan husen, medel (NACH-B, längdviktat)</small></dd>
              </div>
            )}
            <div data-tip="ss.synergy">
              <dt>Synergi</dt>
              <dd>{fmt(summary.synergy)}<small>R² mellan integration på 400 m och 1 200 m</small></dd>
            </div>
            <div data-tip="ss.network">
              <dt>Gatunät</dt>
              <dd>
                {((summary.lengthM || 0) / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} km
                <small>{(summary.linkCount || 0).toLocaleString('sv-SE')} segment i rutan{summary.network === 'streets' ? ', bara gator' : ', med gång- och cykelvägar'}</small>
              </dd>
            </div>
            {summary.weighting && (
              <div data-tip="ss.meanSpeed">
                <dt>Över 30 km/h</dt>
                <dd>
                  {fmt(100 * summary.weighting.fastShare, 0)} %
                  <small>av gatulängden{summary.weighting.speedSource === 'osm' ? ' (OSM, NVDB saknas)' : ' (NVDB)'}</small>
                </dd>
              </div>
            )}
            {summary.weighting && (
              <div data-tip="ss.meanService">
                <dt>Service</dt>
                <dd>
                  {summary.weighting.serviceShareMean == null ? '–' : `${fmt(100 * summary.weighting.serviceShareMean, 0)} %`}
                  <small>av behoven ({summary.weighting.serviceGroupLabel}) inom gångavstånd, medel</small>
                </dd>
              </div>
            )}
            {formWeighted && form?.formFullShare != null && (
              <div data-tip="ss.meanForm">
                <dt>Tydligt gaturum</dt>
                <dd>
                  {fmt(100 * form.formFullShare, 0)} %
                  <small>av gatulängden (höjd/bredd 0,5 eller mer)</small>
                </dd>
              </div>
            )}
          </dl>
          {summary.weighting?.serviceError && (
            <p className="os-disclaimer">Servicen kunde inte hämtas ({summary.weighting.serviceError}) – den viktade analysen väger bara in hastigheten.</p>
          )}
          {summary.weighting?.serviceCapped && (
            <p className="os-disclaimer">Servicesökningen nådde taket för antal anrop – i de tätaste delarna kan service saknas i viktningen.</p>
          )}

          {summary.between?.error && (
            <p className="os-disclaimer">Genomströmningen mellan husen kunde inte beräknas: {summary.between.error}</p>
          )}

          {streetTables(`Högst ${m.name.toLowerCase()} · ${RADIUS_LABELS[radius]}`,
            `Lägst ${m.name.toLowerCase()} · ${RADIUS_LABELS[radius]}`, m.short)}

          <details className="ss-explain">
            <summary data-tip="ss.explain">Så läser du kartan</summary>
            <p>
              <strong>Space syntax</strong> (Bill Hillier och Julienne Hanson, UCL, 1970-talet) ser
              gatunätet som ett nätverk av rum och mäter hur varje gata ligger i förhållande till
              alla andra. Gatunätets form styr en stor del av gångflödena – <em>naturlig rörelse</em> –
              och butiker, kaféer och service söker sig till gator där flödet redan finns.
            </p>
            <p>
              <strong>Integration</strong> visar hur lätt en gata nås från alla andra gator inom
              radien, räknat i svängar: få och små vinkeländringar ger högt värde. Integrerade gator
              (rött) är de självklara målpunkterna; segregerade (blått) är lugna men kan sakna
              naturlig uppsikt.
            </p>
            <p>
              <strong>Genomströmning</strong> visar hur ofta gatan ligger på den enklaste vägen
              mellan andra gator – de stråk folk passerar på väg någon annanstans. De röda linjerna
              bildar områdets förgrundsnät.
            </p>
            <p>
              <strong>Mellan husen</strong> är genomströmning där resorna börjar och slutar i husen:
              varje väg räknas efter våningsytan i båda ändar (Lantmäteriets byggnader, våningar från
              höjden i Naturvårdsverkets NMD, ca 3 m per våning). Vanlig genomströmning låter varje
              gatustycke skicka och ta emot lika många resor – en gångväg i parken lika många som en
              gata med sexvåningshus. Här lyfts stråken mellan täta kvarter, medan gångvägar genom
              parker och gator utan hus sjunker. Metoden följer MIT:s Urban Network Analysis och
              Chalmers mått på tillgänglig täthet.
            </p>
            <p>
              <strong>Radien</strong> mäts längs gatunätet: 400 m (ca 5 min promenad) visar
              kvarterets struktur, 1 200 m (ca 15 min) stadsdelens. Hela stadens (global) integration
              beräknas inte – modellen omfattar rutan plus 1 200 m runt om.
            </p>
            <p>
              <strong>Viktad</strong> (kryssrutan) väger in {formWeighted ? 'tre' : 'två'} egenskaper hos varje gata:
              värdet × <em>hastighetsfaktor</em> × <em>servicefaktor</em>
              {formWeighted && <> × <em>gaturumsfaktor</em></>}. Lägre hastighet ger en
              bättre stadsgata: faktorn är 1,0 upp till 30 km/h (och på gågator och gångvägar),
              0,75 vid 40, 0,5 vid 50 och 0,25 vid 60 km/h, enligt Trafikverkets NVDB.
              Servicefaktorn bygger på samma behov som Promenadavstånd: andelen av målgruppens
              vardagsbehov (livsmedel, kollektivtrafik, grönyta, vård …) som finns inom sitt
              gångavstånd längs gatunätet, från 0,5 (inget) till 1,5 (allt). Prickarna visar
              platserna från Google som räknats.
              {formWeighted && (
                <> Gaturumsfaktorn bygger på gaturummet (fliken Gaturum): 1,0 där fasadernas höjd är
                  minst halva gatubredden, ned till 0,7 vid höjd/bredd 0,2 och på gator utan hus på båda
                  sidor. Gångvägar och gator där höjden är okänd räknas som 1,0.</>
              )}
            </p>
            <p>
              <strong>Färgerna</strong> är rangordning inom rutan i tiondelar, så två områden kan
              inte jämföras på färgen. Jämför i stället medelvärdena ovan: NAIN och NACH är
              normaliserade för att kunna jämföras mellan områden.
            </p>
            <p className="os-disclaimer">
              Gatunät och gångvägar från OpenStreetMap. Trottoarer och cykelbanor längs en gata räknas
              som en del av gatan; broar och tunnlar korsar inte gatorna under och över. Motorvägar
              och gator där gående inte får gå ingår inte. Metoden följer Hillier, Yang & Turner
              (2012), vinkelanalys av gatusegment som i depthmapX.
            </p>
          </details>
          <p className="os-disclaimer os-attribution">
            {ATTRIBUTION}
            {summary.osmTimestamp && ` · Gatunätet enligt OpenStreetMap ${new Date(summary.osmTimestamp).toLocaleDateString('sv-SE')}`}
          </p>
        </div>
      )}

      {previous.length > 0 && (
        <div className="os-previous">
          <h3>Tidigare analyser av gatunätet</h3>
          <ul>
            {previous.map((a) => (
              <li key={a.analysisId}>
                <button onClick={() => loadPreviousAnalysis(a.analysisId)} disabled={isAnalyzing} data-tip="ss.previous">
                  {a.name ? `${a.name} - ` : ''}{new Date(a.timestamp).toLocaleString('sv-SE')} - {a.linkCount ?? a.syntax?.linkCount ?? '?'} segment
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// The page: map script, top bar and heading around the tool (which needs the
// loaded Maps API for its first render).
const SpaceSyntax = () => {
  const { isLoaded, loadError } = useLoadScript({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries });
  if (loadError) return <div className="error">Kartan kunde inte laddas: {loadError.message}</div>;
  if (!isLoaded) return <div className="status">Laddar karta…</div>;
  return (
    <>
      <TopBar />
      <div className="space-syntax ut-page">
        <div className="ut-page-head">
          <div className="ut-eyebrow">Gatunät</div>
          <h1>Hur hänger gatunätet ihop?</h1>
          <p className="ut-lede">
            Se vilka gator som nås med få svängar från omgivningen (integration) och vilka som
            bär de genomgående flödena (genomströmning) – space syntax-metoden från University
            College London, beräknad på gatunätet i OpenStreetMap.
          </p>
        </div>
        <SpaceSyntaxTool />
      </div>
    </>
  );
};

export default SpaceSyntax;
